 /*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, OfficeRuntime */

import {
  getAllSignatures,
  createSignature,
  updateSignature,
  deleteSignature,
  getRenderedSignature,
  getUserProfileValues,
  Signature,
  ConsentRequiredError,
  SignatureNotTargetedError,
} from "../helpers/signature-api";
import { showMessage, clearMessage, hideMessage } from "../helpers/message-helper";
import { appendWatermarkIfNeeded, fetchCapabilities, formatPlanLabel, getAllowedAttachmentFormats, isImageAttachmentAllowed, isImageBase64Allowed, isImageUrlAllowed, type Capabilities } from "../helpers/capabilities-helper";
import { resolveInlineAttachmentsForCompose } from "../utils/resolveInlineAttachments";
import { SET_SIGNATURE_ASYNC_MAX_CHARS, SIGNATURE_SAVE_MAX_CHARS, isOverSaveLimit, isInWarnBand } from "../utils/signatureCharLimits";
import { runWithApiBusy, type ApiBusyLock } from "../utils/apiBusy";
import {
  getAccessToken,
  mirrorTokenToLocalStorageIfPresent,
  setStoredToken,
  isAuthCallback,
  handleAuthCallback,
  AUTH_POPUP_MESSAGE_TYPE,
  clearStoredToken,
  getConsentGiven,
  setConsentGiven,
  clearConsentGiven,
  startLoginInTaskpaneRedirect,
  getAuthDialogStartUrl,
  fetchOAuthBridgeTokenOnce,
  startLoginInPopup,
  navigatePreparedPopupToOAuth,
  generateOAuthBridgeKey,
  isOAuthLaunchDialogState,
  isOfficeDialogChild,
  getLaunchAuthOAuthBridgeKeyFromUrl,
  isLaunchAuthDialogModeFromUrl,
  isLaunchAuthConsentRequiredFromUrl,
} from "../helpers/auth-helper";
import { detectOutlookHostKind, shouldHandOffLaunchTokenViaBridge } from "../helpers/outlook-host";

import { getLocaleFromOffice, setLocale, getLocale, t } from "./i18n";
import {
  mountDebugPanel,
  dbgInfo,
  dbgOk,
  dbgWarn,
  dbgError,
  dbgStep,
  subscribeAutorunDebugFromCompose,
} from "../mobile-debug";
import { initSignatureEditor, getSignatureEditorContent } from "./signature-editor";
import { initCampaignsPane, setEditorMainTabsVisible, setActiveMainTab, updateEditorMainTabsVisibility, openCampaignsPane, openCampaignForm } from "./campaigns-pane";
import { isFreCarouselOpen, setFreCarouselShellOpen } from "./fre-carousel-state";
import { updateSignatureCampaignMappings } from "../helpers/campaign-api";
import {
  initSignatureFormCampaignMapping,
  getSignatureFormSelectedCampaignIds,
  refreshSignatureFormMappingLength,
  getSignatureFormMappingLengthStatus,
} from "./signature-campaign-mapping-ui";
import { mountAudiencePicker, type AudiencePicker } from "./audience-targeting-ui";
import { sanitizeSignatureHtml } from "../utils/signatureHtmlSanitizer";
import { prepareAssetImagesForDisplay, resolveAssetImagesForDisplay } from "../utils/signatureAssetImages";
import { renderProfilePlaceholders } from "../utils/profilePlaceholders";
import { GRAPH_USER_FIELD_IDS, graphFieldToken, graphUserFieldOptions } from "../utils/graphUserFields";
import {
  ensureImageDimensionsForEmail,
  prepareSignatureHtmlForOutlookCompose,
  prepareClassicOutlookSignatureInsert,
} from "../utils/signatureOutlookComposeHtml";
import { API_BASE_URL, PREMIUM_BASE_URL } from "../config";
import { saveDefaultSignatureIdToApi, fetchDefaultSignatureDetailsFromApi, fetchResolvedOrgDefaultSignatureIds, type DefaultSignatureKind, type ResolvedOrgDefaultSignatureIds } from "../helpers/user-settings-api";
import {
  isLikelySafariBrowser,
  isMobileOutlookHost,
  isOutlookWebAppInDesktopSafari,
  isWebOrNewOutlookHost,
} from "../autorun/autorun-sso";

declare const SUNNYSIGN_APP_VERSION: string;

const LAUNCH_AUTH_DIALOG_QUERY = "sunnysign_launch_auth";
const LAUNCH_AUTH_MESSAGE_TYPE = "sunnysign_launch_token";

function isLaunchAuthDialogMode(): boolean {
  return isLaunchAuthDialogModeFromUrl();
}

function isLaunchAuthConsentRequired(): boolean {
  return isLaunchAuthConsentRequiredFromUrl();
}

function getOAuthStateFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get("state") || "";
  } catch {
    return "";
  }
}

/** Launch-Auth auch nach OAuth-Redirect erkennen (iOS löscht oft sessionStorage/localStorage). */
function isLaunchAuthOAuthContext(): boolean {
  if (isOfficeDialogChild()) return true;
  if (isLaunchAuthDialogMode()) return true;
  if (isLaunchAuthSessionActive()) return true;
  return isOAuthLaunchDialogState(getOAuthStateFromUrl());
}

/**
 * OAuth-Redirect löscht sessionStorage (Outlook Web / WKWebView).
 * Flag wird daher in sessionStorage UND localStorage gespeichert.
 */
const LAUNCH_AUTH_SESSION_KEY = "sunnysign_launch_auth_dialog";

function setLaunchAuthSessionFlag(): void {
  try { sessionStorage.setItem(LAUNCH_AUTH_SESSION_KEY, "1"); } catch { /* ignore */ }
  try { localStorage.setItem(LAUNCH_AUTH_SESSION_KEY, "1"); } catch { /* ignore */ }
}

function isLaunchAuthSessionActive(): boolean {
  try { if (sessionStorage.getItem(LAUNCH_AUTH_SESSION_KEY) === "1") return true; } catch { /* ignore */ }
  try { if (localStorage.getItem(LAUNCH_AUTH_SESSION_KEY) === "1") return true; } catch { /* ignore */ }
  return false;
}

function clearLaunchAuthSessionFlag(): void {
  try { sessionStorage.removeItem(LAUNCH_AUTH_SESSION_KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(LAUNCH_AUTH_SESSION_KEY); } catch { /* ignore */ }
}

function getLaunchAuthStatusMessage(): string {
  try {
    const lang = String((Office.context as { displayLanguage?: string } | undefined)?.displayLanguage || "").toLowerCase();
    if (lang.startsWith("de")) {
      return "Anmeldung wird abgeschlossen …\n\nBitte dieses Fenster offen lassen, bis es sich von selbst schließt.";
    }
  } catch {
    /* ignore */
  }
  return "Finishing sign-in …\n\nPlease keep this window open until it closes on its own.";
}

function ensureLaunchAuthStatusOverlay(): void {
  if (typeof document === "undefined" || !document.body) return;
  if (document.getElementById("sunnysign-launch-auth-status")) return;
  const el = document.createElement("div");
  el.id = "sunnysign-launch-auth-status";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.textContent = getLaunchAuthStatusMessage();
  el.style.cssText =
    "position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;font:16px/1.45 system-ui,-apple-system,BlinkMacSystemFont,sans-serif;background:#fff;color:#222;z-index:2147483647;box-sizing:border-box;white-space:pre-line;";
  document.body.appendChild(el);
}

function applyLaunchAuthShellEarly(): void {
  try {
    if (typeof document === "undefined" || !document.body) return;
    if (
      isOfficeDialogChild() ||
      isLaunchAuthDialogMode() ||
      isLaunchAuthSessionActive() ||
      (isAuthCallback() && isLaunchAuthOAuthContext())
    ) {
      document.body.classList.add("sunnysign-launch-auth-shell");
      ensureLaunchAuthStatusOverlay();
    }
  } catch {
    /* ignore */
  }
}

type LaunchAuthParentPayload =
  | { success: true; access_token: string; expires_in?: number; tokenViaBridge?: boolean }
  | { success: false };

async function sendLaunchAuthResultToParent(payload: LaunchAuthParentPayload): Promise<boolean> {
  const lite =
    payload.success && payload.tokenViaBridge
      ? { type: LAUNCH_AUTH_MESSAGE_TYPE, success: true as const, tokenViaBridge: true }
      : payload.success
        ? {
            type: LAUNCH_AUTH_MESSAGE_TYPE,
            success: true as const,
            access_token: payload.access_token,
            expires_in: payload.expires_in,
          }
        : { type: LAUNCH_AUTH_MESSAGE_TYPE, success: false as const };
  const str = JSON.stringify(lite);
  const delays = [0, 32, 64, 128, 250, 500, 750, 1000, 1500, 2000, 3000, 4000];
  for (const d of delays) {
    if (d) await new Promise((r) => setTimeout(r, d));
    if (tryMessageParentOnce(str)) {
      clearLaunchAuthSessionFlag();
      return true;
    }
  }
  return false;
}

/** messageParent: immer targetOrigin zuerst (Outlook Web benötigt es), dann ohne als Fallback. */
function tryMessageParentOnce(payloadStr: string): boolean {
  try {
    const ui = Office.context?.ui;
    if (!ui || typeof ui.messageParent !== "function") return false;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (origin) {
      try {
        // targetOrigin zuerst – funktioniert in Outlook Web und WKWebView
        (ui.messageParent as (msg: string, opts?: { targetOrigin: string }) => void)(payloadStr, { targetOrigin: origin });
        dbgOk("messageParent(targetOrigin) OK");
        return true;
      } catch (e) {
        dbgWarn(`messageParent(targetOrigin) fehlgeschlagen: ${String(e)}`);
      }
    }
    try {
      ui.messageParent(payloadStr);
      dbgOk("messageParent() (kein targetOrigin) OK");
      return true;
    } catch (e2) {
      dbgError(`messageParent() fehlgeschlagen: ${String(e2)}`);
      return false;
    }
  } catch (e) {
    dbgError(`messageParent Ausnahme: ${String(e)}`);
    return false;
  }
}

async function showLaunchAuthManualCloseHint(): Promise<void> {
  ensureLaunchAuthStatusOverlay();
  const st = document.getElementById("sunnysign-launch-auth-status");
  if (st) {
    const lang = String((Office.context as { displayLanguage?: string } | undefined)?.displayLanguage || "").toLowerCase();
    st.textContent = lang.startsWith("de")
      ? "Anmeldung erfolgreich! Schlie\u00DFen Sie dieses Fenster (\u00ABX\u00BB) und \u00F6ffnen Sie SunnySign erneut."
      : "Login successful! Close this window (X) and reopen SunnySign.";
  }
  clearLaunchAuthSessionFlag();
}

type AuthCallbackSuccess = Extract<Awaited<ReturnType<typeof handleAuthCallback>>, { success: true }>;

/**
 * OAuth-Callback im Mobil-Launch-Dialog: Token an Parent senden, nie die volle Taskpane-UI anzeigen.
 * @returns true wenn der Callback vollständig behandelt wurde (Caller soll return).
 */
async function finishLaunchAuthOAuthCallback(
  result: AuthCallbackSuccess | { success: false; wasLaunchDialogOAuth?: boolean }
): Promise<boolean> {
  const inLaunch =
    isOfficeDialogChild() || isLaunchAuthOAuthContext() || result.wasLaunchDialogOAuth === true;
  if (!inLaunch) return false;

  if (!result.success) {
    dbgError("OAuth-Callback fehlgeschlagen – Launch-Auth-Dialog");
    await sendLaunchAuthResultToParent({ success: false });
    clearLaunchAuthSessionFlag();
    return true;
  }

  dbgStep("Launch-Auth: Sende Token an Parent (messageParent)…");
  const useBridgeOnly =
    !!result.oauthBridgeKey && shouldHandOffLaunchTokenViaBridge(detectOutlookHostKind());
  const sent = await sendLaunchAuthResultToParent(
    useBridgeOnly && result.oauthBridgeKey
      ? { success: true, access_token: result.access_token, expires_in: result.expires_in, tokenViaBridge: true }
      : { success: true, access_token: result.access_token, expires_in: result.expires_in }
  );
  if (sent) return true;

  dbgError("Launch-Auth: messageParent fehlgeschlagen – manueller Hinweis");
  await showLaunchAuthManualCloseHint();
  return true;
}

const LOGO_LIGHT = "assets/logo/SunnySignLogoSmallLightMode.svg";
const LOGO_DARK = "assets/logo/SunnySignLogoSmallDarkMode.svg";
const LOGO_FULL_LIGHT = "assets/logo/SunnySignLogoLightMode.svg";
const LOGO_FULL_DARK = "assets/logo/SunnySignLogoDarkMode.svg";
const SHOW_TASKPANE_BY_DEFAULT_KEY = "sunnysign_show_taskpane_by_default";
/** RoamingSettings-Key für Standardsignatur (wird überall automatisch gesetzt) */
export const DEFAULT_SIGNATURE_ID_KEY = "sunnysign_default_signature_id";
/** RoamingSettings-Key für Antwort-/Weiterleitungs-Standardsignatur */
export const REPLY_FORWARD_DEFAULT_SIGNATURE_ID_KEY = "sunnysign_reply_forward_default_signature_id";
/** Erstnutzung (ISO-Datum) – Premium-Teaser erst nach X Minuten anzeigen */
const FIRST_USE_DATE_KEY = "sunnysign_first_use_date";
/** Premium-Teaser (Hero, Sneak Peek) erst nach dieser Anzahl Tage anzeigen */
const PREMIUM_TEASER_AFTER_DAYS = 14;
/** SessionStorage-Key: Hero wurde mit X geschlossen (bleibt in dieser Sitzung ausgeblendet) */
const PREMIUM_HERO_DISMISSED_KEY = "sunnysign_premium_hero_dismissed";

let cachedProPurchaseUrl: string | null = null;
let cachedUserPlan: "free" | "pro" | null = null;
let cachedCapabilities: Capabilities | null = null;

function signatureEditorImageOptions(): {
  allowImageUrl: boolean;
  allowImageBase64: boolean;
  allowImageAsAttachment: boolean;
  allowedAttachmentFormats: string[];
  onContentChange: () => void;
} {
  return {
    allowImageUrl: isImageUrlAllowed(cachedCapabilities),
    allowImageBase64: isImageBase64Allowed(cachedCapabilities),
    allowImageAsAttachment: isImageAttachmentAllowed(cachedCapabilities),
    allowedAttachmentFormats: getAllowedAttachmentFormats(cachedCapabilities),
    onContentChange: () => refreshSignatureFormLengthUi(),
  };
}

/** Live length warn / disable save; also refreshes campaign mapping estimate. */
function refreshSignatureFormLengthUi(): void {
  const content = (signatureEditorApi ? signatureEditorApi.getContent() : getSignatureEditorContent()).trim();
  const len = content.length;
  const hint = document.getElementById("signature-length-hint");
  const submitBtn = document.getElementById("signature-submit-btn") as HTMLButtonElement | null;
  const overSave = isOverSaveLimit(len);
  const inWarn = isInWarnBand(len);

  if (hint) {
    if (overSave) {
      hint.hidden = false;
      hint.className = "sunnysign-length-hint sunnysign-length-hint--error";
      hint.textContent = t("msg.tooLong", {
        len: String(len),
        saveMax: String(SIGNATURE_SAVE_MAX_CHARS),
        max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
      });
    } else if (inWarn) {
      hint.hidden = false;
      hint.className = "sunnysign-length-hint sunnysign-length-hint--warn";
      hint.textContent = t("msg.lengthWarn", {
        len: String(len),
        saveMax: String(SIGNATURE_SAVE_MAX_CHARS),
        max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
      });
    } else {
      hint.hidden = true;
      hint.textContent = "";
    }
  }

  let mappingOk = true;
  if (shouldShowCampaignMappingInForm()) {
    mappingOk = refreshSignatureFormMappingLength(t);
  }

  if (submitBtn) {
    submitBtn.disabled = overSave || !mappingOk;
  }
}

function getAdminPortalUrl(): string {
  return `${window.location.origin}/admin/home`;
}

function openAdminPortal(): void {
  const url = getAdminPortalUrl();
  try {
    if (typeof Office !== "undefined" && Office.context?.ui?.openBrowserWindow) {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch {
    /* fallback */
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

function canEditOrgTemplates(): boolean {
  return cachedCapabilities?.canEditOrgTemplates === true;
}

/** Free: alle Tenant-Nutzer dürfen Signaturen bearbeiten; Pro: nur Template-Editor-Gruppe. */
function canEditSignatures(): boolean {
  const caps = cachedCapabilities;
  if (!caps) return false;
  const plan = caps.plan?.toLowerCase() ?? cachedUserPlan ?? "free";
  if (plan !== "pro") return true;
  return caps.canEditOrgTemplates === true;
}

function canAccessAdminPortal(): boolean {
  return cachedCapabilities?.canAccessAdminPortal === true;
}

function isProSeatBlocked(): boolean {
  const caps = cachedCapabilities;
  if (!caps || caps.plan?.toLowerCase() !== "pro") return false;
  if (caps.canUseSignatures === false) return true;
  if (caps.seatBlocked === true) return true;
  return caps.isLicensed === false;
}

function renderNoLicenseState(): void {
  const container = document.getElementById("signatures-container");
  if (!container) return;
  container.innerHTML = `<p class="sunnysign-no-license" role="status">${escapeHtml(t("list.noLicense"))}</p>`;
}

function applyLicenseGateUi(): void {
  const blocked = isProSeatBlocked();
  if (isFreCarouselOpen()) {
    hideAppUiBehindFre();
    return;
  }
  document.querySelectorAll("#signature-list-section .sunnysign-list-actions").forEach((el) => {
    (el as HTMLElement).style.display = blocked ? "none" : "";
  });

  const defaultControls = document.getElementById("default-signature-controls");
  if (defaultControls) defaultControls.style.display = blocked ? "none" : "flex";
  const onSettings = isSettingsSectionVisible();
  setBottomChromeVisible(!blocked && !onSettings);
  updateHeaderAuthUi();

  if (!blocked) {
    applyRoleBasedListUi();
  }
}

function applyRoleBasedListUi(): void {
  if (isFreCarouselOpen()) return;
  const addBtn = document.getElementById("add-signature-button");
  if (addBtn) addBtn.style.display = canEditSignatures() ? "" : "none";

  updateEditorMainTabsVisibility(canEditOrgTemplates());
}

function openProPurchasePage(): void {
  const url = cachedProPurchaseUrl?.trim();
  if (!url) return;
  try {
    if (typeof Office !== "undefined" && Office.context?.ui?.openBrowserWindow) {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch {
    /* fallback below */
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

function applyProPurchaseLinks(): void {
  const url = cachedProPurchaseUrl?.trim() || "";
  document.querySelectorAll<HTMLAnchorElement>("[data-pro-purchase-link]").forEach((el) => {
    el.href = url || "#";
    el.setAttribute("aria-disabled", url ? "false" : "true");
    el.style.pointerEvents = url ? "" : "none";
    el.style.opacity = url ? "" : "0.55";
  });
}

function updateProUpgradeButton(plan: string): void {
  const btn = document.getElementById("pro-upgrade-btn");
  if (!btn) return;
  const showUpgrade = plan === "free" && !!cachedProPurchaseUrl?.trim();
  btn.style.display = showUpgrade ? "inline-block" : "none";
}
/** RoamingSettings: First-Run-Carousel (FRE) wurde abgeschlossen oder übersprungen */
const FRE_CAROUSEL_DONE_KEY = "sunnysign_fre_carousel_done";

const OAUTH_BC_NAME = "sunnysign-oauth-sync";

/** State aus buildLoginUrl (Popup/Brücke) — im System-Popup fehlt oft Office-Mailbox → nicht über OWA-Host erkennbar. */
function oauthStateIndicatesSunnysignPopupFlow(): boolean {
  try {
    const state = new URLSearchParams(window.location.search).get("state");
    if (!state) return false;
    return state.startsWith("SSPO1");
  } catch {
    return false;
  }
}

/**
 * OAuth-Callback im separaten Browser-Popup: dort ist `isOutlookWebAppInDesktopSafari()` oft false
 * (kein Mailbox-Kontext). Schließen + Token nur anhand Brücken-Key, Fenstername oder Popup-State
 * (letzteres nur mit Safari-UA, damit andere Clients unverändert bleiben).
 */
function shouldFinishSafariOwaOAuthInPopup(result: {
  wasPopupOAuth?: boolean;
  oauthBridgeKey?: string | null;
}): boolean {
  /* Popup-Abschluss per BroadcastChannel nur in Desktop-Safari (nicht Chrome/Edge/Fx). */
  if (!isLikelySafariBrowser()) return false;
  const namedOAuthWindow =
    typeof window !== "undefined" &&
    (window.name === "sunnysign_oauth" || window.name === "sunnysign_consent");
  const popupByState = result.wasPopupOAuth === true;
  const hasBridgeKey =
    typeof result.oauthBridgeKey === "string" && /^[a-f0-9]{32}$/i.test(result.oauthBridgeKey);
  if (hasBridgeKey) return true;
  if (namedOAuthWindow) return true;
  if (popupByState) return true;
  if (oauthStateIndicatesSunnysignPopupFlow()) return true;
  return false;
}

function deliverSafariOwaOAuthPopupToOpener(
  payload: { access_token: string; expires_in?: number },
  dbgDetail: string
): void {
  dbgStep(`Safari OWA: OAuth-Popup abschließen — ${dbgDetail}`);
  try {
    const bc = new BroadcastChannel(OAUTH_BC_NAME);
    bc.postMessage({
      type: AUTH_POPUP_MESSAGE_TYPE,
      access_token: payload.access_token,
      expires_in: payload.expires_in,
    });
    bc.close();
  } catch {
    /* ignore */
  }
  try {
    setConsentGiven();
  } catch {
    /* ignore */
  }
  window.setTimeout(() => {
    try {
      window.close();
    } catch {
      /* ignore */
    }
  }, 80);
}

Office.onReady(async (info) => {
  /*
   * Mobil-OAuth-Dialog (displayDialogAsync): OAuth-Callback sofort behandeln, bevor Taskpane-UI initialisiert wird.
   * Backup falls Microsoft noch auf taskpane.html zurückleitet.
   */
  if (info.host === Office.HostType.Outlook && isAuthCallback()) {
    applyLaunchAuthShellEarly();
    if (isOfficeDialogChild() || isLaunchAuthOAuthContext()) {
      const result = await handleAuthCallback();
      if (await finishLaunchAuthOAuthCallback(result)) return;
      if (isOfficeDialogChild()) return;
    }
  }

  /*
   * Nur Safari OWA: OAuth kehrt im System-Popup zurück, Office meldet dort oft keinen Outlook-Host.
   * Alle anderen Clients: unverändert wie zuvor – Callback in initializeApp() nach Host-Prüfung.
   */
  const earlySafariOwaAuthPopup =
    isAuthCallback() &&
    info.host !== Office.HostType.Outlook &&
    isLikelySafariBrowser() &&
    (isOutlookWebAppInDesktopSafari() || oauthStateIndicatesSunnysignPopupFlow());

  if (earlySafariOwaAuthPopup) {
    subscribeToAuthPopupMessage();
    dbgStep("Safari OWA: OAuth-Callback im Popup (kein Outlook-Host)");
    const result = await handleAuthCallback();
    if (await finishLaunchAuthOAuthCallback(result)) return;

    if (!result.success) {
      dbgError("OAuth-Callback fehlgeschlagen");
      try {
        window.close();
      } catch {
        /* ignore */
      }
      return;
    }

    dbgOk(`OAuth-Callback OK – Token (${result.access_token?.length ?? 0} Zeichen)`);
    if (window.opener) {
      dbgStep("window.opener → postMessage + Fenster schließen");
      try {
        window.opener.postMessage(
          {
            type: AUTH_POPUP_MESSAGE_TYPE,
            access_token: result.access_token,
            expires_in: result.expires_in,
          },
          window.location.origin
        );
      } catch {
        /* ignore */
      }
      try {
        setConsentGiven();
      } catch {
        /* ignore */
      }
      try {
        window.close();
      } catch {
        /* ignore */
      }
      return;
    }

    if (shouldFinishSafariOwaOAuthInPopup(result)) {
      deliverSafariOwaOAuthPopupToOpener(
        { access_token: result.access_token, expires_in: result.expires_in },
        `kein Outlook-Host (statePopup=${result.wasPopupOAuth === true} bridge=${!!result.oauthBridgeKey} name=${String(window.name).slice(0, 24)})`
      );
      return;
    }
  }

  if (info.host !== Office.HostType.Outlook) {
    showMessage(t("msg.outlookOnly"));
    return;
  }

  mountDebugPanel();
  subscribeAutorunDebugFromCompose();
  dbgInfo(`Office.onReady – Host: ${info.host ?? "?"}`);
  applyLaunchAuthShellEarly();
  setLocale(getLocaleFromOffice());
  document.documentElement.lang = getLocale();
  applyOutlookHostClass();
  applyUiStrings();
  applyOfficeTheme();
  subscribeToThemeChange();
  subscribeToAuthPopupMessage();
  registerFreCarouselListeners();
  registerLogoutButtonListener();
  registerConsentTutorialButtonListener();
  registerVersionPopup();
  void initializeApp();
});

function registerVersionPopup(): void {
  const btn = document.getElementById("version-logo-btn");
  const overlay = document.getElementById("version-popup-overlay");
  const closeBtn = document.getElementById("version-popup-close");
  const versionEl = document.getElementById("version-popup-text");
  if (!btn || !overlay) return;

  const version = (typeof SUNNYSIGN_APP_VERSION !== "undefined" && SUNNYSIGN_APP_VERSION) ? SUNNYSIGN_APP_VERSION : "–";
  if (versionEl) versionEl.textContent = `Version ${version}`;

  const showPopup = () => {
    overlay.setAttribute("aria-hidden", "false");
    closeBtn?.focus();
  };
  const hidePopup = () => {
    overlay.setAttribute("aria-hidden", "true");
    btn.focus();
  };

  btn.addEventListener("click", () => showPopup());

  // Klick auf Backdrop (nicht auf das Popup selbst) schließt
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) hidePopup();
  });

  closeBtn?.addEventListener("click", () => hidePopup());

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && overlay.getAttribute("aria-hidden") === "false") hidePopup();
  });
}

/** Prüft anhand einer Hex-Hintergrundfarbe, ob es ein dunkles Theme ist (Outlook unterstützt isDarkTheme nicht). */
function isDarkFromBackgroundColor(hex: string | undefined): boolean {
  if (!hex || typeof hex !== "string") return false;
  const m = hex.match(/^#?([a-fA-F0-9]{6})$/);
  if (!m) return false;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 0.5;
}

/**
 * Klassisches Outlook (Win/Mac) meldet hostName "Outlook", neues Outlook für Windows
 * "newOutlookWindows", Outlook im Browser "OutlookWebApp". Im neuen Outlook und im Browser
 * zeigt der Host schon eigenes Branding, deshalb entfällt dort das Header-Logo.
 */
function applyOutlookHostClass(): void {
  try {
    const hostName = String(Office.context?.mailbox?.diagnostics?.hostName ?? "");
    const withoutHeaderLogo = hostName === "newOutlookWindows" || hostName === "OutlookWebApp";
    document.documentElement.classList.toggle("sunnysign-no-header-logo", withoutHeaderLogo);
  } catch {
    document.documentElement.classList.remove("sunnysign-no-header-logo");
  }
}

function applyOfficeTheme(): void {
  const body = document.body;
  const logo = document.getElementById("header-logo") as HTMLImageElement | null;
  const popupLogo = document.querySelector(".sunnysign-version-popup__logo") as HTMLImageElement | null;
  const settingsLogo = document.getElementById("settings-page-logo") as HTMLImageElement | null;
  const officeTheme = typeof Office !== "undefined" ? (Office.context as any).officeTheme : undefined;
  let isDark = false;
  if (officeTheme) {
    if (officeTheme.isDarkTheme === true) {
      isDark = true;
    } else {
      // Outlook: isDarkTheme wird nicht unterstützt – anhand bodyBackgroundColor erkennen
      isDark = isDarkFromBackgroundColor(officeTheme.bodyBackgroundColor);
    }
  }
  const theme = isDark ? "dark" : "light";
  body.setAttribute("data-theme", theme);
  if (logo) logo.src = isDark ? LOGO_DARK : LOGO_LIGHT;
  if (popupLogo) popupLogo.src = isDark ? LOGO_DARK : LOGO_LIGHT;
  if (settingsLogo) settingsLogo.src = isDark ? LOGO_FULL_DARK : LOGO_FULL_LIGHT;
}

/** Wendet alle Übersetzungen auf statische UI-Elemente (data-i18n, data-i18n-placeholder) an. */
function applyUiStrings(): void {
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const key = el.getAttribute("data-i18n");
    if (key) el.textContent = t(key);
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-placeholder]").forEach((el) => {
    const key = el.getAttribute("data-i18n-placeholder");
    if (key) (el as HTMLInputElement | HTMLTextAreaElement).placeholder = t(key);
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((el) => {
    const key = el.getAttribute("data-i18n-aria");
    if (key) {
      const label = t(key);
      el.setAttribute("aria-label", label);
      el.setAttribute("title", label);
    }
  });
  // Premium-Links: Basis-URL + data-premium-path (öffnet in neuem Tab)
  const base = (PREMIUM_BASE_URL || "").replace(/\/+$/, "");
  document.querySelectorAll<HTMLElement>("[data-premium-path]").forEach((el) => {
    const path = el.getAttribute("data-premium-path") || "";
    if (el instanceof HTMLAnchorElement && base && path) {
      el.href = base + (path.startsWith("/") ? path : "/" + path);
      el.target = "_blank";
      el.rel = "noopener noreferrer";
    }
  });
}

/** Speichert das Datum der ersten Nutzung (einmalig), damit Premium-Teaser erst nach X Tagen erscheinen. */
function ensureFirstUseDateSet(): void {
  const rs = Office.context?.roamingSettings;
  if (!rs) return;
  const existing = rs.get(FIRST_USE_DATE_KEY);
  if (!existing) {
    rs.set(FIRST_USE_DATE_KEY, new Date().toISOString());
    rs.saveAsync(() => {});
  }
}

/** Tage seit der ersten Nutzung (0 wenn noch nicht gesetzt). */
function getDaysSinceFirstUse(): number {
  const rs = Office.context?.roamingSettings;
  const raw = rs?.get(FIRST_USE_DATE_KEY);
  if (!raw || typeof raw !== "string") return 0;
  const first = new Date(raw);
  if (Number.isNaN(first.getTime())) return 0;
  const now = new Date();
  return Math.floor((now.getTime() - first.getTime()) / (24 * 60 * 60 * 1000));
}

/** true = Premium-Teaser (Hero, Sneak Peek) anzeigen (nur Free, ab 14 Tagen). */
function shouldShowPremiumTeaser(): boolean {
  return cachedUserPlan === "free" && getDaysSinceFirstUse() >= PREMIUM_TEASER_AFTER_DAYS;
}

/** Zeigt oder versteckt Premium-Teaser (nur Free, ab 14 Tagen). Hero kann per X geschlossen werden (SessionStorage). */
function applyPremiumTeaserVisibility(): void {
  const show = shouldShowPremiumTeaser();
  const heroDismissed = typeof sessionStorage !== "undefined" && sessionStorage.getItem(PREMIUM_HERO_DISMISSED_KEY) === "1";
  document.querySelectorAll(".sunnysign-premium-hero-wrap").forEach((el) => {
    (el as HTMLElement).style.display = show && !heroDismissed ? "" : "none";
  });
  document.querySelectorAll(".sunnysign-premium-preview-teaser").forEach((el) => {
    (el as HTMLElement).style.display = show ? "" : "none";
  });
}

function subscribeToThemeChange(): void {
  if (
    typeof Office !== "undefined" &&
    Office.context?.mailbox &&
    (Office.context.mailbox as any).addHandlerAsync
  ) {
    (Office.context.mailbox as any).addHandlerAsync(
      (Office as any).EventType?.OfficeThemeChanged,
      () => applyOfficeTheme(),
      () => {}
    );
  }
}

/**
 * Bei gepinntem Taskpane: Wenn der Nutzer zu einer anderen E-Mail wechselt (z. B. zweites
 * neues E-Mail-Fenster), Ansicht auf Signatur-Liste zurücksetzen und Liste neu laden.
 * Verhindert leere Ansicht beim Wechsel zwischen Compose-Fenstern.
 */
function subscribeToItemChanged(): void {
  if (
    typeof Office !== "undefined" &&
    Office.context?.mailbox &&
    (Office.context.mailbox as any).addHandlerAsync &&
    (Office as any).EventType?.ItemChanged
  ) {
    (Office.context.mailbox as any).addHandlerAsync(
      (Office as any).EventType.ItemChanged,
      () => {
        showSignatureList();
      },
      () => {}
    );
  }
}

/** Verarbeitet Nachricht vom Office-Dialog (Erfolg oder Fehler). */
function readSharedOAuthExpirySeconds(): number | undefined {
  try {
    const expMs = localStorage.getItem("sunnysign_token_expires");
    if (!expMs) return undefined;
    const ms = parseInt(expMs, 10);
    if (Number.isNaN(ms)) return undefined;
    return Math.max(0, Math.round((ms - Date.now()) / 1000));
  } catch {
    return undefined;
  }
}

function onAuthTokenReceived(data: { type: string; access_token?: string; expires_in?: number; success?: boolean }): void {
  if (!data || data.type !== AUTH_POPUP_MESSAGE_TYPE) return;
  if (data.success === false) {
    showMessage(t("msg.authFailed"));
    return;
  }
  if (!data.access_token) return;
  setStoredToken(data.access_token, data.expires_in);
  setConsentGiven();
  setFormConsentBannerVisible(false);

  if (isOutlookWebAppInDesktopSafari()) {
    const consentSection = document.getElementById("consent-section");
    const formSection = document.getElementById("signature-form-section");
    const freSection = document.getElementById("fre-carousel-section");
    const consentBlocking = consentSection?.style.display === "block";
    const freBlocking = freSection?.style.display === "block";
    const formOnly = !!formSection && formSection.style.display !== "none";

    /*
     * Nach Brücke/Popup: Consent- oder FRE-Ansicht muss immer in die App wechseln.
     * Nur der seltene Fall „nur Formular sichtbar, weder Consent noch FRE“: Hinweis, Liste aktualisieren.
     */
    if (formOnly && !consentBlocking && !freBlocking) {
      showMessage(t("form.consentGrantedHint"));
      void refreshFormProfileFieldsAfterConsent();
      updateHeaderAuthUi();
      loadSignatures();
      return;
    }
    showAppSection();
    registerAppEventListeners();
    subscribeToItemChanged();
    loadSignatures();
    return;
  }

  const consentSection = document.getElementById("consent-section");
  if (consentSection && consentSection.style.display === "block") {
    showAppSection();
    registerAppEventListeners();
    subscribeToItemChanged();
    loadSignatures();
  } else {
    showMessage(t("form.consentGrantedHint"));
    refreshFormProfileFieldsAfterConsent();
  }
}

function subscribeToAuthPopupMessage(): void {
  window.addEventListener("message", (event) => {
    if (event.origin !== window.location.origin) return;
    onAuthTokenReceived(event.data);
  });
  if (!isOutlookWebAppInDesktopSafari()) return;
  /* Safari OWA: opener oft null nach Redirect; Token ggf. über storage / BroadcastChannel */
  window.addEventListener("storage", (ev: StorageEvent) => {
    if (ev.key !== "sunnysign_access_token" || !ev.newValue) return;
    dbgInfo("OAuth: Token über localStorage synchronisiert (Login-Popup)");
    onAuthTokenReceived({
      type: AUTH_POPUP_MESSAGE_TYPE,
      access_token: ev.newValue,
      expires_in: readSharedOAuthExpirySeconds(),
    });
  });
  try {
    const bc = new BroadcastChannel(OAUTH_BC_NAME);
    bc.onmessage = (event: MessageEvent) => {
      const d = event.data as { type?: string; access_token?: string; expires_in?: number; success?: boolean } | undefined;
      if (!d || d.type !== AUTH_POPUP_MESSAGE_TYPE) return;
      dbgInfo("OAuth: BroadcastChannel vom Login-Popup");
      onAuthTokenReceived({
        type: AUTH_POPUP_MESSAGE_TYPE,
        access_token: d.access_token,
        expires_in: d.expires_in,
        success: d.success,
      });
    };
  } catch {
    /* BroadcastChannel nicht verfügbar */
  }
}

let oauthBridgePollTimer: ReturnType<typeof setInterval> | null = null;
/** Verhindert parallele GETs: sonst kann ein zweiter Request das Token schon „wegnehmen“ und nur {pending:true} liefern. */
let oauthBridgePollInFlight = false;

function stopOAuthBridgePolling(): void {
  if (oauthBridgePollTimer != null) {
    clearInterval(oauthBridgePollTimer);
    oauthBridgePollTimer = null;
  }
  oauthBridgePollInFlight = false;
}

/**
 * Safari OWA: Taskpane (iframe) und Popup teilen keinen localStorage ([Microsoft Learn](https://learn.microsoft.com/de-de/office/dev/add-ins/develop/auth-with-office-dialog-api)).
 * Token kommt per kurzlebiger API-Brücke + Polling ins Taskpane.
 */
function startOAuthBridgePolling(bridgeKey: string): void {
  stopOAuthBridgePolling();
  const deadline = Date.now() + 180_000;
  oauthBridgePollTimer = setInterval(() => {
    void (async () => {
      if (oauthBridgePollInFlight) return;
      if (Date.now() > deadline) {
        stopOAuthBridgePolling();
        dbgWarn("OAuth-Brücke: Timeout beim Warten auf Popup-Token");
        return;
      }
      oauthBridgePollInFlight = true;
      try {
        const cb = encodeURIComponent(String(Date.now()));
        const r = await fetch(
          `${API_BASE_URL}/api/auth-bridge/${encodeURIComponent(bridgeKey)}?_=${cb}`,
          {
            method: "GET",
            credentials: "omit",
            cache: "no-store",
            headers: { Accept: "application/json", "Cache-Control": "no-cache", Pragma: "no-cache" },
          }
        );
        if (r.status !== 200) return;
        const j = (await r.json()) as Record<string, unknown>;
        const token =
          (typeof j.access_token === "string" && j.access_token) ||
          (typeof j.accessToken === "string" && j.accessToken) ||
          "";
        /* Nur abbrechen, wenn wirklich kein Token: pending allein darf ein paralleles Rennen nicht verhindern. */
        if (!token && j.pending === true) return;
        if (!token) return;
        const expRaw = j.expires_in ?? j.expiresIn;
        let expiresIn: number | undefined;
        if (typeof expRaw === "number" && Number.isFinite(expRaw)) expiresIn = expRaw;
        else if (typeof expRaw === "string") {
          const n = parseInt(expRaw, 10);
          if (Number.isFinite(n)) expiresIn = n;
        }
        stopOAuthBridgePolling();
        dbgOk(`OAuth-Brücke: Token vom Server (${token.length} Zeichen)`);
        onAuthTokenReceived({
          type: AUTH_POPUP_MESSAGE_TYPE,
          access_token: token,
          expires_in: expiresIn,
        });
      } catch (e) {
        dbgWarn(`OAuth-Brücke GET: ${String(e)}`);
      } finally {
        oauthBridgePollInFlight = false;
      }
    })();
  }, 400);
}

function isFreCarouselDone(): boolean {
  try {
    const rs = Office.context?.roamingSettings;
    return rs?.get(FRE_CAROUSEL_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

function setFreCarouselDone(): void {
  const rs = Office.context?.roamingSettings;
  if (!rs) return;
  try {
    rs.set(FRE_CAROUSEL_DONE_KEY, "1");
    rs.saveAsync(() => {});
  } catch { /* saveAsync not available in dialog contexts */ }
}

/** Outlook Web + Safari: vor Anzeige der App prüfen, ob das gespeicherte Token von der API akzeptiert wird (vermeidet „kurz App, dann 401 → Login“). */
async function probeStoredTokenAcceptedByApi(): Promise<boolean> {
  const token = getAccessToken();
  if (!token) return false;
  try {
    const r = await fetch(`${API_BASE_URL}/api/signatures`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      credentials: "omit",
    });
    if (r.status === 401) return false;
    return r.ok;
  } catch {
    return true;
  }
}

/** Synchron beim Klick: leeres Popup, damit nach async PKCE die OAuth-URL gesetzt werden kann (Safari blockt sonst oft). */
function openOauthBlankPopupForSafariOwa(): Window | null {
  if (!isOutlookWebAppInDesktopSafari()) return null;
  try {
    return window.open("about:blank", "sunnysign_oauth", "width=520,height=640");
  } catch {
    return null;
  }
}

/** Office SSO im gleichen Taskpane (kein zweites Fenster), nur wenn noch kein API-Token. */
// DEBUG: vollständige SSO-Instrumentierung
async function tryOfficeSsoIfNeeded(opts?: {
  forceConsentPrompt?: boolean;
  /** Weist Office an, Konto hinzuzufügen/zu wechseln (kein OAuth-Redirect / kein Browser). */
  forceAddAccount?: boolean;
  /**
   * Nur true, wenn der Nutzer aktiv „Anmelden“ gewählt hat. Bei false kein Sign-In-Prompt –
   * verhindert im klassischen Outlook oft Systembrowser / gebrochene SSO-Session bei Hintergrund-Aufrufen
   * (z. B. parallel zum Launch-Event).
   */
  interactiveSignIn?: boolean;
}): Promise<void> {
  if (getAccessToken()) { dbgInfo("SSO: Token bereits vorhanden – überspringe"); return; }
  if (typeof OfficeRuntime === "undefined" || !OfficeRuntime.auth?.getAccessToken) {
    dbgWarn("SSO: OfficeRuntime.auth nicht verfügbar (kein SSO-Support auf diesem Host)");
    return;
  }
  try {
    const consentAlreadyGiven = getConsentGiven();
    const allowConsent = opts?.forceConsentPrompt === true ? true : !consentAlreadyGiven;
    dbgStep(`SSO: getAccessToken – allowSignIn:${opts?.interactiveSignIn ?? false} allowConsent:${allowConsent} forceAdd:${opts?.forceAddAccount ?? false}`);
    const tokenOptions: Office.AuthOptions = {
      allowSignInPrompt: opts?.interactiveSignIn === true,
      allowConsentPrompt: allowConsent,
      forMSGraphAccess: false,
    };
    if (opts?.forceAddAccount) {
      tokenOptions.forceAddAccount = true;
    }
    const officeToken = await OfficeRuntime.auth.getAccessToken(tokenOptions);
    if (officeToken) {
      dbgOk(`SSO: Token erhalten (Länge: ${officeToken.length})`);
      setStoredToken(officeToken);
      setConsentGiven();
    } else {
      dbgWarn("SSO: getAccessToken lieferte leeres Token");
    }
  } catch (ssoErr) {
    const e = ssoErr as { code?: number; errorCode?: number; message?: string };
    const code = e?.code ?? e?.errorCode;
    const ssoCodeMap: Record<number, string> = {
      13001: "WebApplicationInfo fehlt/nicht deployed",
      13002: "Admin Consent fehlt",
      13003: "Kein AAD-Konto im Outlook-Host",
      13005: "User hat Consent abgelehnt",
      13006: "SSO nur in Office-Hosts",
      13007: "Host unterstützt SSO nicht",
      13008: "Vorherige SSO-Anfrage läuft noch",
      13012: "Kein Netzwerk / ungültiger App-Status",
    };
    const codeStr = code != null ? `${code} – ${ssoCodeMap[code] ?? "unbekannter Code"}` : "kein Code";
    dbgError(`SSO fehlgeschlagen: ${codeStr} | ${e?.message ?? ""}`);
  }
}

/**
 * Öffnet Microsoft-Login über displayDialogAsync (echtes Fenster, kein Iframe → kein X-Frame-Options Problem).
 * Das sessionStorage/localStorage-Flag überlebt den OAuth-Redirect zuverlässig.
 */
function startLoginViaOfficeDialog(requireConsent: boolean, oauthPopupCarrier?: Window | null): Promise<void> {
  return new Promise<void>((resolve) => {
    const safariOwa = isOutlookWebAppInDesktopSafari();

    /**
     * Nur Safari OWA: separates Browser-Popup + kurzlebige API-Brücke (Taskpane pollt GET).
     * Andere Hosts: Legacy-Popup (postMessage/opener), keine `/api/auth-bridge`-Polls.
     */
    const runSafariBridgePopup = (
      launch: (bridgeKey: string) => Promise<void>,
      onLaunchReject?: () => void
    ): void => {
      const bridgeKey = generateOAuthBridgeKey();
      startOAuthBridgePolling(bridgeKey);
      void launch(bridgeKey)
        .then(() => resolve())
        .catch(() => {
          stopOAuthBridgePolling();
          if (onLaunchReject) {
            onLaunchReject();
            return;
          }
          void startLoginInTaskpaneRedirect(requireConsent).then(resolve);
        });
    };

    const runLegacyPopupOnly = (): void => {
      dbgStep("OAuth-Fallback: Popup ohne Server-Brücke");
      void startLoginInPopup(requireConsent, "")
        .then(() => resolve())
        .catch(() => void startLoginInTaskpaneRedirect(requireConsent).then(resolve));
    };

    const finishWithSafariBridgePopup = (): void => {
      dbgStep(`Safari OWA: OAuth-Popup + API-Brücke (requireConsent: ${requireConsent})`);
      if (oauthPopupCarrier && !oauthPopupCarrier.closed) {
        runSafariBridgePopup(
          (bk) => navigatePreparedPopupToOAuth(oauthPopupCarrier, requireConsent, bk),
          () => runSafariBridgePopup((bk) => startLoginInPopup(requireConsent, bk))
        );
      } else {
        runSafariBridgePopup((bk) => startLoginInPopup(requireConsent, bk));
      }
    };

    /** Nur Nicht-Safari: Dialog fehlgeschlagen oder keine Dialog-API → Legacy-Popup. */
    const finishWithPopupFallback = (): void => {
      runLegacyPopupOnly();
    };

    /*
     * Safari + OWA: Office zeigt oft „Sicherheitszone … localhost:3000 …“ und öffnet keinen Dialog.
     * Microsoft empfiehlt die Dialog-API generell; hier ist sie praktisch unbrauchbar → nur Popup + API-Brücke.
     */
    if (safariOwa) {
      dbgStep(
        "Safari OWA: kein displayDialogAsync (Sicherheitszonen) → OAuth-Popup + API-Brücke"
      );
      finishWithSafariBridgePopup();
      return;
    }

    const ui = Office.context?.ui;
    if (!ui?.displayDialogAsync) {
      dbgWarn("displayDialogAsync nicht verfügbar → Popup-Fallback");
      finishWithPopupFallback();
      return;
    }
    const oauthBridgeKey = generateOAuthBridgeKey();
    const dialogUrl = getAuthDialogStartUrl(requireConsent, oauthBridgeKey);
    dbgStep(`Öffne Login-Dialog (requireConsent: ${requireConsent})`);
    ui.displayDialogAsync(dialogUrl, { height: 60, width: 40, promptBeforeOpen: false }, (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        dbgError(`displayDialogAsync fehlgeschlagen: ${asyncResult.error?.message ?? ""} → Popup-Fallback`);
        finishWithPopupFallback();
        return;
      }
      dbgOk("Login-Dialog geöffnet – warte auf Anmeldung...");
      const dialog = asyncResult.value;
      let settled = false;

      const finish = (success: boolean, token?: string, expiresIn?: number) => {
        if (settled) return;
        settled = true;
        try { dialog.close(); } catch { /* ignore */ }
        if (success && token) {
          dbgOk(`Token erhalten (${token.length} Zeichen) – lade App`);
          setStoredToken(token, expiresIn);
          setConsentGiven();
          setFormConsentBannerVisible(false);
          const cs = document.getElementById("consent-section");
          if (cs && cs.style.display === "block") {
            showAppSection(); registerAppEventListeners(); subscribeToItemChanged(); loadSignatures();
          } else {
            showMessage(t("form.consentGrantedHint"));
            void refreshFormProfileFieldsAfterConsent();
          }
        } else {
          dbgError("Login abgebrochen oder fehlgeschlagen");
          showMessage(t("msg.authFailed"));
        }
        resolve();
      };

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if (!arg || typeof arg !== "object" || !("message" in arg)) return;
        const { message } = arg as { message: string };
        dbgInfo(`DialogMessage: ${String(message ?? "").slice(0, 80)}`);
        try {
          const msg = typeof message === "string" ? JSON.parse(message) : message;
          if (msg?.type === LAUNCH_AUTH_MESSAGE_TYPE) {
            if (msg.success && msg.tokenViaBridge) {
              void fetchOAuthBridgeTokenOnce(oauthBridgeKey).then((bridge) => {
                finish(!!bridge?.access_token, bridge?.access_token, bridge?.expires_in);
              });
            } else {
              finish(!!msg.success && !!msg.access_token, msg.access_token, msg.expires_in);
            }
          }
        } catch (e) { dbgError(`DialogMessage parse: ${String(e)}`); }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
        if (!arg || typeof arg !== "object" || !("error" in arg)) return;
        const err = (arg as { error: number }).error;
        dbgWarn(`DialogEvent: error=${err}`);
        if (err === 12006 || err === 12003) { settled = true; resolve(); }
      });
    });
  });
}

/**
 * Login-Flow: SSO → Dialog (kein X-Frame-Options Problem, kein hängendes Fenster).
 */
async function startLoginInSameTaskpane(options?: {
  forceConsentPromptForSso?: boolean;
  oauthRequireConsent?: boolean;
  oauthPopupCarrier?: Window | null;
}): Promise<void> {
  if (!isOutlookWebAppInDesktopSafari()) {
    dbgStep("startLoginInSameTaskpane: versuche SSO...");
    await tryOfficeSsoIfNeeded(options?.forceConsentPromptForSso ? { forceConsentPrompt: true } : undefined);
    if (getAccessToken()) {
      dbgOk("SSO OK → zeige App"); showAppSection(); registerAppEventListeners(); subscribeToItemChanged(); loadSignatures(); return;
    }
  } else {
    dbgStep("startLoginInSameTaskpane: Safari OWA → kein Office-SSO (Nutzeraktion: OAuth-Popup/Dialog)");
  }
  const requireConsent = options?.oauthRequireConsent !== undefined ? options.oauthRequireConsent : !getConsentGiven();
  dbgStep(`SSO fehlgeschlagen → Login-Dialog (requireConsent: ${requireConsent})`);
  await startLoginViaOfficeDialog(requireConsent, options?.oauthPopupCarrier);
}

/**
 * Anderes Konto: SSO mit forceAddAccount, sonst Login-Dialog.
 */
async function startLoginOtherAccountSameTaskpane(oauthPopupCarrier?: Window | null): Promise<void> {
  clearStoredToken(); clearConsentGiven();
  if (!isOutlookWebAppInDesktopSafari()) {
    dbgStep("Anderes Konto: SSO forceAddAccount...");
    if (typeof OfficeRuntime !== "undefined" && typeof OfficeRuntime.auth?.getAccessToken === "function") {
      await tryOfficeSsoIfNeeded({ forceConsentPrompt: true, forceAddAccount: true, interactiveSignIn: true });
      if (getAccessToken()) {
        dbgOk("SSO anderes Konto OK"); showAppSection(); registerAppEventListeners(); subscribeToItemChanged(); loadSignatures(); return;
      }
    }
  } else {
    dbgStep("Safari OWA: anderes Konto → direkt OAuth-Popup");
  }
  dbgStep("SSO fehlgeschlagen → Login-Dialog");
  await startLoginViaOfficeDialog(true, oauthPopupCarrier);
}

async function attemptEnterAppViaSso(options?: { skipLoadSignatures?: boolean }): Promise<boolean> {
  if (isOutlookWebAppInDesktopSafari()) {
    dbgInfo("Safari OWA: kein automatisches Office-SSO beim Öffnen/Anpinnen");
  } else {
    await tryOfficeSsoIfNeeded();
  }
  if (!getAccessToken()) return false;
  if (isOutlookWebAppInDesktopSafari()) {
    dbgStep("Safari OWA: prüfe gespeichertes Token gegen API …");
    const apiOk = await probeStoredTokenAcceptedByApi();
    if (!apiOk) {
      clearStoredToken();
      dbgWarn("Safari OWA: Token ungültig/abgelaufen → Anmelde-Ansicht");
      return false;
    }
  }
  showAppSection();
  registerAppEventListeners();
  subscribeToItemChanged();
  if (!options?.skipLoadSignatures) {
    loadSignatures();
  }
  return true;
}

function isSectionDisplayNone(id: string): boolean {
  return document.getElementById(id)?.style.display === "none";
}

/** „?“ + Settings-Zahnrad: sichtbar unter der Hauptansicht, oberhalb des Footers (nicht im FRE). */
function setBottomChromeVisible(visible: boolean): void {
  const chrome = document.getElementById("sunnysign-bottom-chrome");
  if (chrome) chrome.style.display = visible ? "flex" : "none";
}

function isSettingsSectionVisible(): boolean {
  return document.getElementById("settings-section")?.style.display === "flex";
}

function setSettingsSectionVisible(visible: boolean): void {
  const section = document.getElementById("settings-section");
  if (section) section.style.display = visible ? "flex" : "none";
}

function updatePlanBadgeVisibilityClass(): void {
  const stack = document.getElementById("plan-badge-stack");
  if (!stack) return;
  const shown = stack.style.display !== "none";
  document.documentElement.classList.toggle("sunnysign-plan-badge-visible", shown);
}

function updateHeaderAuthUi(): void {
  const hasToken = !!getAccessToken();
  const chrome = document.getElementById("sunnysign-bottom-chrome");
  const chromeVisible = chrome && chrome.style.display !== "none";
  const settingsVisible = isSettingsSectionVisible();

  const bottomBtn = document.getElementById("logout-button-bottom");
  if (bottomBtn) bottomBtn.style.display = hasToken && settingsVisible ? "inline-flex" : "none";

  const adminBtn = document.getElementById("admin-portal-button");
  if (adminBtn) {
    adminBtn.style.display = hasToken && chromeVisible && canAccessAdminPortal() ? "inline-flex" : "none";
  }

  const settingsOpenBtn = document.getElementById("settings-open-button");
  if (settingsOpenBtn) {
    settingsOpenBtn.style.display = hasToken && chromeVisible && !settingsVisible ? "inline-flex" : "none";
  }

  const tutorialBtn = document.getElementById("consent-tutorial-button");
  if (tutorialBtn) {
    tutorialBtn.style.display = chromeVisible ? "inline-flex" : "none";
  }

  if (settingsVisible) {
    updateSettingsEnvBadge();
  }

  const header = document.querySelector(".sunnysign-header");
  if (header) {
    const withoutHeaderLogo = document.documentElement.classList.contains("sunnysign-no-header-logo");
    header.classList.toggle("sunnysign-header--shell-hidden", withoutHeaderLogo);
  }

  if (!hasToken) {
    hidePlanBadge();
  }
}

/** TEST vs PROD anhand Host / API-URL (localhost → test). */
function resolveDeployEnvLabel(): "test" | "prod" {
  const host = typeof window !== "undefined" ? String(window.location.host || "").toLowerCase() : "";
  const api = String(API_BASE_URL || "").toLowerCase();
  const haystack = `${host} ${api}`;
  if (
    haystack.includes("app.test.sunnysign.app") ||
    haystack.includes("app-test.sunnysign.app") ||
    haystack.includes("api.test.sunnysign") ||
    haystack.includes("localhost") ||
    haystack.includes("127.0.0.1")
  ) {
    return "test";
  }
  if (haystack.includes("app.sunnysign.app") || haystack.includes("api.sunnysign.app")) {
    return "prod";
  }
  return haystack.includes("test") ? "test" : "prod";
}

function updateSettingsEnvBadge(): void {
  const badge = document.getElementById("settings-env-badge");
  if (!badge) return;
  const env = resolveDeployEnvLabel();
  badge.textContent = env;
  badge.classList.remove("sunnysign-env-badge--test", "sunnysign-env-badge--prod");
  badge.classList.add(env === "test" ? "sunnysign-env-badge--test" : "sunnysign-env-badge--prod");
  badge.setAttribute("title", env === "test" ? "TEST environment" : "PROD environment");
}

function hidePlanBadge(): void {
  const stack = document.getElementById("plan-badge-stack");
  const badge = document.getElementById("plan-badge");
  if (stack) stack.style.display = "none";
  updatePlanBadgeVisibilityClass();
  if (!badge) return;
  badge.textContent = "";
  badge.classList.remove("sunnysign-plan-badge--free", "sunnysign-plan-badge--pro");
  cachedProPurchaseUrl = null;
  cachedUserPlan = null;
  cachedCapabilities = null;
  applyRoleBasedListUi();
  updateProUpgradeButton("pro");
  applyProPurchaseLinks();
  applyPremiumTeaserVisibility();
}

async function refreshPlanBadge(): Promise<void> {
  const stack = document.getElementById("plan-badge-stack");
  const badge = document.getElementById("plan-badge");
  if (!stack || !badge || !getAccessToken()) {
    hidePlanBadge();
    return;
  }

  /* Netzwerkfehler (DNS/offline) darf keine unbehandelte Rejection werfen — Aufruf erfolgt teils per void. */
  const caps = await fetchCapabilities().catch((e) => {
    console.warn("[SunnySign] Capabilities nicht geladen:", e);
    return null;
  });
  if (!caps) {
    hidePlanBadge();
    return;
  }

  cachedProPurchaseUrl = caps.marketplaceManageUrl?.trim() || null;
  const plan = caps.plan?.toLowerCase() === "pro" ? "pro" : "free";
  cachedUserPlan = plan;
  cachedCapabilities = caps;
  badge.textContent = formatPlanLabel(plan);
  badge.classList.remove("sunnysign-plan-badge--free", "sunnysign-plan-badge--pro");
  badge.classList.add(plan === "pro" ? "sunnysign-plan-badge--pro" : "sunnysign-plan-badge--free");
  stack.style.display = "flex";
  updatePlanBadgeVisibilityClass();
  updateProUpgradeButton(plan);
  applyProPurchaseLinks();
  applyPremiumTeaserVisibility();
  if (isFreCarouselOpen()) {
    hideAppUiBehindFre();
    return;
  }
  applyRoleBasedListUi();
  applyLicenseGateUi();
  if (isProSeatBlocked()) {
    renderNoLicenseState();
  } else if (signatures.length > 0) {
    renderSignatureList();
  }
}

function hideFreCarouselSection(): void {
  const fre = document.getElementById("fre-carousel-section");
  if (!fre) return;
  fre.style.display = "none";
  fre.setAttribute("aria-hidden", "true");
  setFreCarouselShellOpen(false);
}

/** App-UI ausblenden, solange das Tutorial sichtbar ist. */
function hideAppUiBehindFre(): void {
  document.getElementById("consent-section")!.style.display = "none";
  document.getElementById("signature-list-section")!.style.display = "none";
  document.getElementById("signature-form-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("campaigns-list-section")!.style.display = "none";
  document.getElementById("campaign-form-section")!.style.display = "none";
  setSettingsSectionVisible(false);
  setEditorMainTabsVisible(false);
  setBottomChromeVisible(false);
}

type FreSlideDef = {
  titleKey: string;
  bodyKey: string;
  /** Ein oder mehrere Screenshots; Reihenfolge oben → unten bei mehreren */
  images: string[];
  /** Zusätzliche CSS-Klasse am Bild-Container */
  wrapClass?: string;
  /** Pro-Feature: bei Free mit Upgrade-Hinweis kennzeichnen */
  proFeature?: boolean;
};

/** Tutorial zeigt Pro-Ansichten für alle; Upgrade-Hinweis nur ohne aktives Pro. */
function shouldMarkFreProFeatures(): boolean {
  const plan = (cachedCapabilities?.plan ?? cachedUserPlan)?.toLowerCase();
  if (plan === "pro" && !isProSeatBlocked()) return false;
  return true;
}

function getFreSlideDefs(): FreSlideDef[] {
  const de = getLocale() === "de";
  const shot = (enFile: string, deFile: string) => `assets/sunnySignScreenshots/${de ? deFile : enFile}`;
  const shared = (file: string) => `assets/sunnySignScreenshots/${file}`;
  return [
    {
      titleKey: "fre.slide.start.title",
      bodyKey: "fre.slide.start.body",
      images: [shot("startScreenNormalUserEnglish.png", "startScreenNormalUserGerman.png")],
    },
    {
      titleKey: "fre.slide.menu.title",
      bodyKey: "fre.slide.menu.body",
      images: [
        shot("SignatureMenuEnglish.png", "SignatureMenuGerman.png"),
        shot("adminSettingsEnglish.png", "adminSettingsGerman.png"),
      ],
    },
    {
      titleKey: "fre.slide.editor.title",
      bodyKey: "fre.slide.editor.body",
      images: [shot("editSignatureEnglishPro.png", "editSignatureGermanPro.png")],
    },
    {
      titleKey: "fre.slide.profile.title",
      bodyKey: "fre.slide.profile.body",
      images: [shared("Profilefield.png"), shared("ConditionalRange.png")],
    },
    {
      titleKey: "fre.slide.availability.title",
      bodyKey: "fre.slide.availability.body",
      images: [shot("availabilityProEnglish.png", "availabilityProGerman.png")],
      proFeature: true,
    },
    {
      titleKey: "fre.slide.campaigns.title",
      bodyKey: "fre.slide.campaigns.body",
      images: [shot("CampaignAssignmentProEnglish.png", "CampaignAssignmentProGerman.png")],
      proFeature: true,
    },
    {
      titleKey: "fre.slide.defaults.title",
      bodyKey: "fre.slide.defaults.body",
      images: [shot("DefaultSignatureEnglish.png", "DefaultSignatureGerman.png")],
    },
    {
      titleKey: "fre.slide.pin.title",
      bodyKey: "fre.slide.pin.body",
      images: [shared("pin.png"), shot("pinEnglish.png", "pinGerman.png")],
      wrapClass: "sunnysign-fre__img-wrap--pin",
    },
  ];
}

let freSlideIndex = 0;

const FRE_NAV_CHEVRON_L = `<svg class="sunnysign-fre__nav-chevron" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>`;
const FRE_NAV_CHEVRON_R = `<svg class="sunnysign-fre__nav-chevron" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>`;

function updateFreNavUi(): void {
  const defs = getFreSlideDefs();
  const prev = document.getElementById("fre-prev") as HTMLButtonElement | null;
  const next = document.getElementById("fre-next") as HTMLButtonElement | null;
  if (!prev || !next) return;
  const last = freSlideIndex >= defs.length - 1;
  prev.disabled = freSlideIndex === 0;
  prev.innerHTML = FRE_NAV_CHEVRON_L;
  prev.setAttribute("aria-label", t("fre.back"));
  if (last) {
    next.className = "sunnysign-btn sunnysign-btn--primary sunnysign-fre__nav-btn sunnysign-fre__nav-btn--cta";
    next.innerHTML = "";
    next.textContent = t("fre.getStarted");
    next.setAttribute("aria-label", t("fre.getStarted"));
  } else {
    next.className = "sunnysign-fre__nav-iconbtn sunnysign-fre__nav-iconbtn--primary sunnysign-fre__nav-next";
    next.innerHTML = FRE_NAV_CHEVRON_R;
    next.setAttribute("aria-label", t("fre.next"));
  }
}

function goToFreSlide(index: number): void {
  const defs = getFreSlideDefs();
  freSlideIndex = Math.max(0, Math.min(index, defs.length - 1));
  document.querySelectorAll(".sunnysign-fre__slide").forEach((el, i) => {
    const h = el as HTMLElement;
    h.style.display = i === freSlideIndex ? "" : "none";
    h.setAttribute("aria-hidden", i === freSlideIndex ? "false" : "true");
  });
  document.querySelectorAll(".sunnysign-fre__dot").forEach((d, i) => {
    d.classList.toggle("sunnysign-fre__dot--active", i === freSlideIndex);
    d.setAttribute("aria-selected", i === freSlideIndex ? "true" : "false");
  });
  updateFreNavUi();
}

function formatFreText(text: string, highlightPro: boolean): string {
  const escaped = escapeHtml(text);
  if (!highlightPro) return escaped;
  return escaped.replace(/\bPro\b/g, '<span class="sunnysign-fre__pro-hl">Pro</span>');
}

function renderFreSlides(): void {
  const container = document.getElementById("fre-slides");
  const dots = document.getElementById("fre-dots");
  if (!container || !dots) return;
  const defs = getFreSlideDefs();
  const total = String(defs.length);
  const markUpgrade = shouldMarkFreProFeatures();
  container.innerHTML = defs
    .map((def, i) => {
      const titleAlt = escapeHtml(t(def.titleKey));
      const wrapCls = `sunnysign-fre__img-wrap${def.wrapClass ? ` ${def.wrapClass}` : ""}`;
      const multi = def.images.length > 1;
      const mediaCls = multi ? "sunnysign-fre__media sunnysign-fre__media--stack" : "sunnysign-fre__media";
      const imgBlock = `<div class="${mediaCls}">${def.images
        .map(
          (src, imgIdx) =>
            `<div class="${wrapCls}"><img class="sunnysign-fre__img" src="${src}" alt="${titleAlt}${def.images.length > 1 ? ` (${imgIdx + 1})` : ""}" /></div>`
        )
        .join("")}</div>`;
      const proBadge = def.proFeature
        ? `<span class="sunnysign-fre__pro-badge">${escapeHtml(t("fre.proRequired"))}</span>`
        : "";
      const titleBlock = `<h3 class="sunnysign-fre__slide-title">${titleAlt}${proBadge}</h3>`;
      /* Bei Pro-Badge-Folien kein Text-Highlight – Badge reicht */
      const highlightPro = !def.proFeature;
      const proNote = def.proFeature
        ? `<p class="sunnysign-fre__pro-note">${formatFreText(
            markUpgrade ? t("fre.proRequiredNote") : t("fre.proExclusiveNote"),
            false
          )}</p>`
        : "";
      return `<div class="sunnysign-fre__slide" id="fre-slide-${i}" role="tabpanel" aria-hidden="${i === 0 ? "false" : "true"}">${imgBlock}${titleBlock}${proNote}<p class="sunnysign-fre__slide-body">${formatFreText(t(def.bodyKey), highlightPro)}</p></div>`;
    })
    .join("");
  dots.innerHTML = defs
    .map((_, i) => {
      const label = escapeHtml(t("fre.dotLabel", { n: String(i + 1), total }));
      return `<button type="button" class="sunnysign-fre__dot${i === 0 ? " sunnysign-fre__dot--active" : ""}" role="tab" aria-selected="${i === 0 ? "true" : "false"}" aria-controls="fre-slide-${i}" id="fre-dot-${i}" data-fre-dot="${i}" aria-label="${label}"></button>`;
    })
    .join("");
  dots.querySelectorAll("[data-fre-dot]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = parseInt((btn as HTMLElement).getAttribute("data-fre-dot") || "0", 10);
      goToFreSlide(idx);
    });
  });
}

function showFreCarousel(): void {
  setFreCarouselShellOpen(true);
  hideAppUiBehindFre();
  const fre = document.getElementById("fre-carousel-section");
  if (fre) {
    fre.style.display = "flex";
    fre.setAttribute("aria-hidden", "false");
  }

  const region = document.getElementById("fre-carousel-region");
  if (region) region.setAttribute("aria-label", t("fre.ariaCarousel"));
  const dotsEl = document.getElementById("fre-dots");
  if (dotsEl) dotsEl.setAttribute("aria-label", t("fre.dotsAria"));

  document.getElementById("fre-skip")!.textContent = t("fre.skip");

  freSlideIndex = 0;
  renderFreSlides();
  goToFreSlide(0);
  updateHeaderAuthUi();
  updateConsentTutorialButtonUi();
}

async function completeFreAndShowConsent(): Promise<void> {
  setFreCarouselDone();
  hideFreCarouselSection();
  if (await attemptEnterAppViaSso()) return;
  showConsentSection();
}

function isFreCarouselVisible(): boolean {
  return isFreCarouselOpen();
}

function advanceFreFromNext(): void {
  const last = freSlideIndex >= getFreSlideDefs().length - 1;
  if (last) void completeFreAndShowConsent();
  else goToFreSlide(freSlideIndex + 1);
}

function onFreCarouselKeydown(e: KeyboardEvent): void {
  if (!isFreCarouselVisible()) return;
  const target = e.target as HTMLElement | null;
  if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
  if (e.key === "ArrowLeft") {
    e.preventDefault();
    goToFreSlide(freSlideIndex - 1);
    return;
  }
  if (e.key === "ArrowRight") {
    e.preventDefault();
    advanceFreFromNext();
  }
}

let freCarouselListenersBound = false;

function registerFreCarouselListeners(): void {
  if (freCarouselListenersBound) return;
  freCarouselListenersBound = true;
  document.getElementById("fre-skip")?.addEventListener("click", () => {
    void completeFreAndShowConsent();
  });
  document.getElementById("fre-prev")?.addEventListener("click", () => {
    goToFreSlide(freSlideIndex - 1);
  });
  document.getElementById("fre-next")?.addEventListener("click", () => {
    advanceFreFromNext();
  });
  document.addEventListener("keydown", onFreCarouselKeydown);
}

function registerLogoutButtonListener(): void {
  document.getElementById("logout-button-bottom")?.addEventListener("click", openLogoutConfirmDialog);
  document.getElementById("logout-confirm-yes")?.addEventListener("click", onLogoutConfirmYes);
  document.getElementById("logout-confirm-no")?.addEventListener("click", closeLogoutConfirmDialog);
  document
    .getElementById("logout-confirm-dialog")
    ?.querySelector(".sunnysign-delete-dialog__backdrop")
    ?.addEventListener("click", closeLogoutConfirmDialog);
}

function openLogoutConfirmDialog(): void {
  const dialog = document.getElementById("logout-confirm-dialog");
  if (dialog) dialog.style.display = "";
}

function closeLogoutConfirmDialog(): void {
  const dialog = document.getElementById("logout-confirm-dialog");
  if (dialog) dialog.style.display = "none";
}

function onLogoutConfirmYes(): void {
  closeLogoutConfirmDialog();
  performLogout();
}

function performLogout(): void {
  clearStoredToken();
  if (!isFreCarouselDone()) {
    showFreCarousel();
  } else {
    showConsentSection();
  }
}

/** „?“ in der gepinnten Leiste: Einführung erneut. */
function registerConsentTutorialButtonListener(): void {
  document.getElementById("consent-tutorial-button")?.addEventListener("click", () => {
    showFreCarousel();
  });
}

function updateConsentTutorialButtonUi(): void {
  const btn = document.getElementById("consent-tutorial-button");
  if (!btn) return;
  const label = t("consent.tutorialAria");
  btn.setAttribute("aria-label", label);
  btn.setAttribute("title", label);
}

let signatures: Signature[] = [];
let cachedResolvedOrgDefaultIds: ResolvedOrgDefaultSignatureIds = { newMail: null, replyForward: null };
let selectedSignature: Signature | null = null;
let editingSignatureId: number | null = null;
let pendingDeleteId: number | null = null;
let signatureEditorApi: {
  getContent: () => string;
  setContent: (html: string) => void;
  didBlockDisallowedContent?: () => boolean;
} | null = null;
let signatureAudiencePicker: AudiencePicker | null = null;
/** Zwischengespeicherte Profilwerte für Vorschau-Ersetzung (Token → Wert, z. B. "{{givenName}}" → "Max"). */
let cachedProfileValues: Record<string, string> = {};


/**
 * Liest Basis-Profildaten direkt aus dem Office-Kontext (kein API-Call nötig).
 * Liefert Anzeigename, Vorname, Nachname und E-Mail aus dem eingeloggten Mailbox-Profil.
 */
/** Anzeigename oder E-Mail aus dem Outlook-Postfach (für Consent-Buttons). */
function getConsentLoginDisplayName(): string {
  try {
    const profile = Office.context?.mailbox?.userProfile;
    if (!profile) return "";
    const displayName = String((profile as any).displayName ?? "").trim();
    if (displayName) return displayName;
    return String((profile as any).emailAddress ?? "").trim();
  } catch {
    return "";
  }
}

function getOfficeContextProfileValues(): Record<string, string> {
  try {
    const profile = Office.context?.mailbox?.userProfile;
    if (!profile) return {};
    const values: Record<string, string> = {};
    // Neben dem Graph-Token auch das frühere deutsche, damit bestehende Signaturen greifen.
    const set = (property: string, legacyToken: string, value: string): void => {
      values[graphFieldToken(property)] = value;
      values[legacyToken] = value;
    };
    const email = (profile as any).emailAddress as string | undefined;
    if (email) set("mail", "{{E-Mail}}", email);
    const displayName = (profile as any).displayName as string | undefined;
    if (displayName) {
      set("displayName", "{{Anzeigename}}", displayName);
      const parts = displayName.trim().split(" ");
      if (parts.length >= 1) set("givenName", "{{Vorname}}", parts[0]);
      if (parts.length >= 2) set("surname", "{{Nachname}}", parts.slice(1).join(" "));
    }
    return values;
  } catch {
    return {};
  }
}

async function initializeApp(): Promise<void> {
  dbgStep("initializeApp() gestartet");
  mirrorTokenToLocalStorageIfPresent();
  applyLaunchAuthShellEarly();

  // 1. OAuth-Callback (Rückkehr von Microsoft im selben Taskpane-WebView) — alle Hosts außer Safari-Popup (s. Office.onReady)
  if (isAuthCallback()) {
    dbgStep("isAuthCallback() = true → verarbeite OAuth-Callback");
    const result = await handleAuthCallback();
    if (await finishLaunchAuthOAuthCallback(result)) return;

    if (!result.success) {
      dbgError("OAuth-Callback fehlgeschlagen");
      showConsentSection(t("msg.authFailed"));
      return;
    }
    dbgOk(`OAuth-Callback OK – Token (${result.access_token?.length ?? 0} Zeichen)`);
    if (window.opener) {
      dbgStep("window.opener vorhanden → postMessage + window.close()");
      window.opener.postMessage(
        {
          type: AUTH_POPUP_MESSAGE_TYPE,
          access_token: result.access_token,
          expires_in: result.expires_in,
        },
        window.location.origin
      );
      setConsentGiven();
      window.close();
      return;
    }
    /*
     * Safari meldet im System-Popup oft trotzdem Outlook als Host → früher Office.onReady-Zweig greift nicht.
     * Ohne die folgende Abzweigung landet das Popup in showAppSection („This add-in only works in Outlook“).
     */
    if (shouldFinishSafariOwaOAuthInPopup(result)) {
      deliverSafariOwaOAuthPopupToOpener(
        { access_token: result.access_token, expires_in: result.expires_in },
        `Outlook-Host im Popup (initializeApp)`
      );
      return;
    }
    if (isLaunchAuthOAuthContext()) {
      dbgError("Launch-Auth: OAuth OK, aber kein Parent-Handoff – kein showAppSection");
      await showLaunchAuthManualCloseHint();
      return;
    }
    showAppSection();
    registerAppEventListeners();
    subscribeToItemChanged();
    loadSignatures();
    return;
  }

  if (isLaunchAuthDialogMode()) {
    dbgStep("LaunchAuth-Dialog-Modus aktiv → starte OAuth-Redirect");
    setLaunchAuthSessionFlag();
    applyLaunchAuthShellEarly();
    const requireConsent = isLaunchAuthConsentRequired();
    const bridgeKey = getLaunchAuthOAuthBridgeKeyFromUrl();
    dbgInfo(`requireConsent: ${requireConsent}, oauth_bridge: ${bridgeKey ? bridgeKey.slice(0, 8) + "…" : "—"}`);
    await startLoginInTaskpaneRedirect(requireConsent, {
      launchDialogFlow: true,
      oauthBridgeKey: bridgeKey ?? undefined,
    });
    return;
  }

  // 2. First-Run-Carousel vor SSO
  if (!getAccessToken() && !isFreCarouselDone()) {
    dbgInfo("Kein Token + FRE nicht abgeschlossen → zeige Einführungs-Carousel");
    showFreCarousel();
    return;
  }

  // 3. Office SSO versuchen
  dbgStep("Versuche Office SSO (OfficeRuntime.auth.getAccessToken)...");
  if (await attemptEnterAppViaSso()) {
    dbgOk("Office SSO erfolgreich → App geöffnet");
    return;
  }

  // 4. Kein Token: Anmelde-Ansicht
  dbgWarn("Office SSO fehlgeschlagen → zeige Anmelde-Ansicht");
  showConsentSection();
}

function showConsentSection(errorMessage?: string): void {
  hideFreCarouselSection();
  document.getElementById("consent-section")!.style.display = "block";
  document.getElementById("signature-list-section")!.style.display = "none";
  document.getElementById("signature-form-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("campaigns-list-section")!.style.display = "none";
  document.getElementById("campaign-form-section")!.style.display = "none";
  setSettingsSectionVisible(false);
  setEditorMainTabsVisible(false);
  setBottomChromeVisible(true);
  const defaultSigControls = document.getElementById("default-signature-controls");
  if (defaultSigControls) defaultSigControls.style.display = "none";
  updateHeaderAuthUi();

  const errEl = document.getElementById("consent-error");
  if (errEl) {
    errEl.style.display = errorMessage ? "block" : "none";
    errEl.textContent = errorMessage || "";
  }

  const btn = document.getElementById("consent-button");
  const otherBtn = document.getElementById("consent-other-account-button");
  if (btn) {
    const requireConsent = !getConsentGiven();
    const officeName = getConsentLoginDisplayName();
    if (officeName) {
      btn.textContent = requireConsent
        ? t("consent.loginAsNameConsent", { name: officeName })
        : t("consent.loginAsNameAgain", { name: officeName });
    } else {
      btn.textContent = requireConsent ? t("consent.button") : t("consent.buttonLoginOnly");
    }
    btn.onclick = () => {
      if (isOutlookWebAppInDesktopSafari()) {
        void startLoginInSameTaskpane({
          oauthRequireConsent: requireConsent,
          oauthPopupCarrier: openOauthBlankPopupForSafariOwa(),
        });
      } else {
        void startLoginInSameTaskpane({ oauthRequireConsent: requireConsent });
      }
    };
  }
  if (otherBtn) {
    otherBtn.textContent = t("consent.otherAccountButton");
    otherBtn.onclick = () => {
      if (isOutlookWebAppInDesktopSafari()) {
        void startLoginOtherAccountSameTaskpane(openOauthBlankPopupForSafariOwa());
      } else {
        void startLoginOtherAccountSameTaskpane();
      }
    };
  }
  updateConsentTutorialButtonUi();
}

function showAppSection(): void {
  if (isOfficeDialogChild()) return;
  if (isFreCarouselOpen()) return;
  setFreCarouselShellOpen(false);
  ensureFirstUseDateSet();
  hideFreCarouselSection();
  document.getElementById("consent-section")!.style.display = "none";
  document.getElementById("signature-list-section")!.style.display = "block";
  document.getElementById("signature-form-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("campaigns-list-section")!.style.display = "none";
  document.getElementById("campaign-form-section")!.style.display = "none";
  setSettingsSectionVisible(false);
  setActiveMainTab("signatures");
  applyLicenseGateUi();
  applyPremiumTeaserVisibility();
  updateHeaderAuthUi();
  void refreshPlanBadge();
  updateConsentTutorialButtonUi();
}

function registerAppEventListeners(): void {
  initCampaignsPane(t, undefined, profileFieldOptions, {
    onAuthLoss: (reason, resume) => {
      if (resume === "campaign-new") {
        recoverSessionAfterAuthLoss(reason, { action: "campaign-new" });
        return;
      }
      if (resume && typeof resume === "object" && resume.kind === "campaign-edit") {
        recoverSessionAfterAuthLoss(reason, { action: "campaign-edit", id: resume.id });
        return;
      }
      recoverSessionAfterAuthLoss(reason, "campaigns");
    },
    onShowSignatures: () => showSignatureList(),
  });

  document.getElementById("add-signature-button")?.addEventListener("click", showSignatureForm);
  document.getElementById("admin-portal-button")?.addEventListener("click", () => {
    openAdminPortal();
  });
  document.getElementById("settings-open-button")?.addEventListener("click", () => {
    void showSettingsSection();
  });
  document.getElementById("settings-home-button")?.addEventListener("click", () => {
    showSignatureList();
  });
  document.getElementById("settings-home-button-bottom")?.addEventListener("click", () => {
    showSignatureList();
  });
  document.getElementById("cancel-button")?.addEventListener("click", showSignatureList);
  document.getElementById("back-to-list-button")?.addEventListener("click", showSignatureList);
  document.getElementById("signature-form")?.addEventListener("submit", handleFormSubmit);
  document.getElementById("insert-signature-button")?.addEventListener("click", insertSignatureIntoEmail);
  document.getElementById("delete-confirm-yes")?.addEventListener("click", onDeleteConfirmYes);
  document.getElementById("delete-confirm-no")?.addEventListener("click", closeDeleteConfirmDialog);
  document.querySelector(".sunnysign-delete-dialog__backdrop")?.addEventListener("click", closeDeleteConfirmDialog);

  document.getElementById("premium-hero-close")?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      sessionStorage.setItem(PREMIUM_HERO_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
    document.querySelectorAll(".sunnysign-premium-hero-wrap").forEach((el) => {
      (el as HTMLElement).style.display = "none";
    });
  });

  document.getElementById("pro-upgrade-btn")?.addEventListener("click", (e) => {
    e.preventDefault();
    openProPurchasePage();
  });

  document.body.addEventListener("click", (e) => {
    const target = (e.target as Element | null)?.closest("[data-pro-purchase-link]");
    if (!target || !cachedProPurchaseUrl?.trim()) return;
    e.preventDefault();
    openProPurchasePage();
  });

  // Immer als „angehakt“ behandeln (Checkbox aus UI entfernt, Verhalten über Pin oben)
  localStorage.setItem(SHOW_TASKPANE_BY_DEFAULT_KEY, "true");

  const showByDefaultCheckbox = document.getElementById("show-taskpane-by-default") as HTMLInputElement | null;
  if (showByDefaultCheckbox) {
    const stored = localStorage.getItem(SHOW_TASKPANE_BY_DEFAULT_KEY);
    showByDefaultCheckbox.checked = stored !== "false";
    showByDefaultCheckbox.addEventListener("change", () => {
      localStorage.setItem(SHOW_TASKPANE_BY_DEFAULT_KEY, showByDefaultCheckbox.checked ? "true" : "false");
    });
  }

  const defaultPicker = document.getElementById("default-signature-picker");
  const replyForwardPicker = document.getElementById("reply-forward-default-signature-picker");
  ensureDefaultSignaturePicker(defaultPicker, "new-mail", DEFAULT_SIGNATURE_ID_KEY);
  ensureDefaultSignaturePicker(replyForwardPicker, "reply-forward", REPLY_FORWARD_DEFAULT_SIGNATURE_ID_KEY);

  document.addEventListener("click", (event) => {
    const target = event.target as Node | null;
    document.querySelectorAll<HTMLElement>(".sunnysign-sig-picker.is-open").forEach((picker) => {
      if (target && picker.contains(target)) return;
      closeSignaturePicker(picker);
    });
  });
}

/**
 * Effektive Standardsignatur-ID — wie Autorun: Server (GET default-signature) schlägt Roaming,
 * damit OWA/Web nach Einstellung in Neu-Outlook das Dropdown korrekt zeigt.
 */
async function resolveEffectiveDefaultSignatureId(kind: DefaultSignatureKind = "new-mail"): Promise<string> {
  const roamingKey =
    kind === "reply-forward" ? REPLY_FORWARD_DEFAULT_SIGNATURE_ID_KEY : DEFAULT_SIGNATURE_ID_KEY;
  const rs = Office.context?.roamingSettings;
  let roamingId = rs ? String(rs.get(roamingKey) ?? "").trim() : "";

  const token = getAccessToken();
  if (token) {
    try {
      const details = await fetchDefaultSignatureDetailsFromApi(API_BASE_URL, token, kind);
      if (details.httpOk) {
        const apiId =
          details.parsedDefaultSignatureId != null ? String(details.parsedDefaultSignatureId) : "";
        if (rs && roamingId !== apiId && roamingId !== (apiId || "__none__")) {
          rs.set(roamingKey, apiId || "__none__");
          rs.saveAsync(() => {});
        }
        return apiId;
      }
    } catch {
      /* Roaming-Fallback */
    }
  }

  if (roamingId === "__none__") return "";
  return roamingId;
}

function resolvedOrgDefaultIdForKind(kind: DefaultSignatureKind): number | null {
  return kind === "reply-forward"
    ? cachedResolvedOrgDefaultIds.replyForward
    : cachedResolvedOrgDefaultIds.newMail;
}

function orgDefaultBadgeHtml(): string {
  return `<span class="sunnysign-sig-picker__badge">${escapeHtml(t("settings.orgDefaultBadge"))}</span>`;
}

function closeSignaturePicker(picker: HTMLElement): void {
  picker.classList.remove("is-open");
  const list = picker.querySelector<HTMLElement>(".sunnysign-sig-picker__list");
  if (list) list.hidden = true;
  const trigger = picker.querySelector<HTMLButtonElement>(".sunnysign-sig-picker__trigger");
  trigger?.setAttribute("aria-expanded", "false");
}

function openSignaturePicker(picker: HTMLElement): void {
  document.querySelectorAll<HTMLElement>(".sunnysign-sig-picker.is-open").forEach((other) => {
    if (other !== picker) closeSignaturePicker(other);
  });
  picker.classList.add("is-open");
  const list = picker.querySelector<HTMLElement>(".sunnysign-sig-picker__list");
  if (list) list.hidden = false;
  const trigger = picker.querySelector<HTMLButtonElement>(".sunnysign-sig-picker__trigger");
  trigger?.setAttribute("aria-expanded", "true");
}

function setSignaturePickerValueDisplay(
  picker: HTMLElement,
  label: string,
  isOrg: boolean
): void {
  const trigger = picker.querySelector<HTMLButtonElement>(".sunnysign-sig-picker__trigger");
  const main = picker.querySelector<HTMLElement>(".sunnysign-sig-picker__trigger-main");
  if (!trigger || !main) return;
  main.innerHTML = `<span class="sunnysign-sig-picker__name">${escapeHtml(label)}</span>${
    isOrg ? orgDefaultBadgeHtml() : ""
  }`;
  trigger.classList.toggle("is-org", isOrg);
}

async function saveDefaultSignatureSelection(
  kind: DefaultSignatureKind,
  roamingKey: string,
  id: string,
  trigger: HTMLButtonElement
): Promise<void> {
  if (defaultSignatureSaveLock.inFlight) return;
  const rs = Office.context?.roamingSettings;
  if (rs) {
    rs.set(roamingKey, id || "__none__");
    rs.saveAsync(() => {});
  }
  const token = getAccessToken();
  if (!token) return;
  const n = parseInt(id, 10);
  const signatureId = !isNaN(n) && n > 0 ? n : null;
  await runWithApiBusy({ buttons: [trigger], lock: defaultSignatureSaveLock }, async () => {
    const ok = await saveDefaultSignatureIdToApi(API_BASE_URL, token, signatureId, kind);
    if (!ok) dbgWarn("Standardsignatur konnte nicht auf dem Server gespeichert werden.");
  });
}

function ensureDefaultSignaturePicker(
  root: HTMLElement | null,
  kind: DefaultSignatureKind,
  roamingKey: string
): void {
  if (!root || root.dataset.wired === "1") return;
  root.dataset.wired = "1";
  root.dataset.kind = kind;
  root.dataset.roamingKey = roamingKey;
  root.dataset.value = "";

  const labelId =
    kind === "reply-forward" ? "reply-forward-default-signature-label" : "default-signature-label";
  root.innerHTML = `
    <button type="button" class="sunnysign-sig-picker__trigger" aria-haspopup="listbox" aria-expanded="false" aria-labelledby="${labelId}">
      <span class="sunnysign-sig-picker__trigger-main">
        <span class="sunnysign-sig-picker__name">${escapeHtml(t("settings.defaultSignatureNone"))}</span>
      </span>
      <span class="sunnysign-sig-picker__chevron" aria-hidden="true"></span>
    </button>
    <ul class="sunnysign-sig-picker__list" role="listbox" hidden></ul>
  `;

  const trigger = root.querySelector<HTMLButtonElement>(".sunnysign-sig-picker__trigger");
  trigger?.addEventListener("click", (event) => {
    event.stopPropagation();
    if (root.classList.contains("is-open")) closeSignaturePicker(root);
    else openSignaturePicker(root);
  });
}

function populateDefaultSignaturePicker(
  root: HTMLElement,
  kind: DefaultSignatureKind,
  savedId: string,
  blockedMessageKey: "msg.defaultNotTargeted" | "msg.replyForwardDefaultNotTargeted"
): void {
  const list = root.querySelector<HTMLElement>(".sunnysign-sig-picker__list");
  const trigger = root.querySelector<HTMLButtonElement>(".sunnysign-sig-picker__trigger");
  if (!list || !trigger) return;

  const resolvedOrgId = resolvedOrgDefaultIdForKind(kind);
  const selectable = signatures.filter((sig) => sig.canUse !== false);
  const hasSaved = selectable.some((s) => String(s.id) === savedId);
  const effectiveId = hasSaved ? savedId : "";
  root.dataset.value = effectiveId;

  const options: Array<{ id: string; name: string; isOrg: boolean }> = [
    { id: "", name: t("settings.defaultSignatureNone"), isOrg: false },
    ...selectable.map((sig) => ({
      id: String(sig.id),
      name: sig.name,
      isOrg: resolvedOrgId != null && sig.id === resolvedOrgId,
    })),
  ];

  list.innerHTML = options
    .map((opt) => {
      const selected = opt.id === effectiveId;
      const classes = [
        "sunnysign-sig-picker__option",
        opt.isOrg ? "is-org" : "",
        selected ? "is-active" : "",
      ]
        .filter(Boolean)
        .join(" ");
      return `<li role="none">
        <button type="button" class="${classes}" role="option" data-value="${escapeHtml(opt.id)}" aria-selected="${
          selected ? "true" : "false"
        }">
          <span class="sunnysign-sig-picker__name">${escapeHtml(opt.name)}</span>
          ${opt.isOrg ? orgDefaultBadgeHtml() : ""}
        </button>
      </li>`;
    })
    .join("");

  const selectedOpt = options.find((o) => o.id === effectiveId) || options[0];
  setSignaturePickerValueDisplay(root, selectedOpt.name, selectedOpt.isOrg);

  if (!hasSaved && savedId && signatures.some((s) => String(s.id) === savedId)) {
    showMessage(t(blockedMessageKey));
  }

  list.querySelectorAll<HTMLButtonElement>(".sunnysign-sig-picker__option").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.stopPropagation();
      const id = String(btn.dataset.value || "");
      const opt = options.find((o) => o.id === id);
      if (!opt) return;
      root.dataset.value = id;
      setSignaturePickerValueDisplay(root, opt.name, opt.isOrg);
      closeSignaturePicker(root);
      void saveDefaultSignatureSelection(kind, root.dataset.roamingKey || "", id, trigger);
    });
  });
}

/**
 * Füllt die Standardsignatur-Dropdowns mit den geladenen Signaturen und stellt gespeicherte Auswahl wieder her.
 */
async function updateDefaultSignatureDropdown(): Promise<void> {
  const defaultPicker = document.getElementById("default-signature-picker");
  const replyForwardPicker = document.getElementById("reply-forward-default-signature-picker");
  if (!defaultPicker && !replyForwardPicker) return;

  ensureDefaultSignaturePicker(defaultPicker, "new-mail", DEFAULT_SIGNATURE_ID_KEY);
  ensureDefaultSignaturePicker(replyForwardPicker, "reply-forward", REPLY_FORWARD_DEFAULT_SIGNATURE_ID_KEY);

  const token = getAccessToken();
  if (token) {
    cachedResolvedOrgDefaultIds = await fetchResolvedOrgDefaultSignatureIds(API_BASE_URL, token);
  } else {
    cachedResolvedOrgDefaultIds = { newMail: null, replyForward: null };
  }

  const [savedNewMailId, savedReplyForwardId] = await Promise.all([
    resolveEffectiveDefaultSignatureId("new-mail"),
    resolveEffectiveDefaultSignatureId("reply-forward"),
  ]);

  if (defaultPicker) {
    populateDefaultSignaturePicker(defaultPicker, "new-mail", savedNewMailId, "msg.defaultNotTargeted");
  }
  if (replyForwardPicker) {
    populateDefaultSignaturePicker(
      replyForwardPicker,
      "reply-forward",
      savedReplyForwardId,
      "msg.replyForwardDefaultNotTargeted"
    );
  }
}

/**
 * Ersetzt alle {{Platzhalter}} in einem HTML-String mit den gecachten Profilwerten
 * und löst dabei „Conditional range“ und „Hide line“ auf.
 * Verbleibende {{...}} (kein Wert in Entra vorhanden) werden geleert → unsichtbar in Vorschau/E-Mail.
 * Im Editor wird diese Funktion NICHT aufgerufen – dort bleiben Platzhalter sichtbar.
 */
function applyProfileValues(html: string, values: Record<string, string> = cachedProfileValues): string {
  return renderProfilePlaceholders(html, values);
}

/**
 * Lädt alle Signaturen vom Backend
 */
function shouldShowCampaignMappingInForm(): boolean {
  return cachedCapabilities?.plan?.toLowerCase() === "pro" && !isProSeatBlocked();
}

function showListSpinner(): void {
  const container = document.getElementById("signatures-container");
  if (!container) return;
  container.innerHTML = `<div class="sunnysign-loading" aria-live="polite" aria-busy="true"><span class="sunnysign-spinner" role="status"></span><span>${t("list.loading")}</span></div>`;
}

/** Wohin nach erfolgreichem SSO-Retry zurückkehren. */
type SessionResume =
  | "signatures"
  | "campaigns"
  | "settings"
  | { action: "signature-new" }
  | { action: "signature-edit"; id: number }
  | { action: "campaign-new" }
  | { action: "campaign-edit"; id: number };

let sessionRecoverInFlight = false;

/**
 * true = Token vorhanden, Aktion fortsetzen.
 * false = Recovery gestartet (SSO); Aufrufer soll abbrechen.
 */
function ensureSessionOrRecover(reason: string, resume: SessionResume = "signatures"): boolean {
  if (getAccessToken()) return true;
  recoverSessionAfterAuthLoss(reason, resume);
  return false;
}

async function applySessionResume(resume: SessionResume): Promise<void> {
  if (resume === "campaigns") {
    await openCampaignsPane();
    return;
  }
  if (resume === "settings") {
    await loadSignatures();
    await showSettingsSection();
    return;
  }
  if (typeof resume === "object") {
    if (resume.action === "signature-new") {
      await loadSignatures();
      await showSignatureForm();
      return;
    }
    if (resume.action === "signature-edit") {
      await loadSignatures();
      await editSignature(resume.id);
      return;
    }
    if (resume.action === "campaign-new") {
      await openCampaignForm();
      return;
    }
    if (resume.action === "campaign-edit") {
      await openCampaignForm(resume.id);
      return;
    }
  }
  await loadSignatures();
}

/** Nach 401 oder lokal fehlendem/abgelaufenem Token: SSO, sonst Login-Ansicht. */
function recoverSessionAfterAuthLoss(reason: string, resume: SessionResume = "signatures"): void {
  if (isFreCarouselOpen()) {
    dbgWarn(`Session-Recovery während Tutorial ignoriert: ${reason}`);
    return;
  }
  if (sessionRecoverInFlight) {
    dbgWarn(`Session-Recovery bereits aktiv – ignoriere: ${reason}`);
    return;
  }
  sessionRecoverInFlight = true;
  dbgWarn(`Session: ${reason} – Token erneuern und erneut laden …`);
  clearStoredToken();
  clearMessage();

  const resumeCampaigns =
    resume === "campaigns" ||
    (typeof resume === "object" && (resume.action === "campaign-new" || resume.action === "campaign-edit"));

  if (resumeCampaigns) {
    document.getElementById("campaigns-list-section")!.style.display = "block";
    document.getElementById("signature-list-section")!.style.display = "none";
    document.getElementById("signature-form-section")!.style.display = "none";
    document.getElementById("campaign-form-section")!.style.display = "none";
    setSettingsSectionVisible(false);
    setActiveMainTab("campaigns");
    setEditorMainTabsVisible(true);
    const container = document.getElementById("campaigns-container");
    if (container) {
      container.innerHTML = `<div class="sunnysign-loading" aria-live="polite" aria-busy="true"><span class="sunnysign-spinner" role="status"></span><span>${t("campaigns.loading")}</span></div>`;
    }
  } else if (resume === "settings") {
    showListSpinner();
  } else {
    showListSpinner();
  }

  void (async () => {
    try {
      if (!(await attemptEnterAppViaSso({ skipLoadSignatures: true }))) {
        showConsentSection(t("msg.sessionExpired"));
        return;
      }
      await applySessionResume(resume);
    } finally {
      sessionRecoverInFlight = false;
    }
  })();
}

function isLocalNotSignedInError(error: unknown): boolean {
  const msg = String((error as { message?: string } | null)?.message ?? error ?? "");
  return (
    /nicht angemeldet/i.test(msg) ||
    /not signed in|please sign in/i.test(msg) ||
    /not_authenticated/i.test(msg)
  );
}

function isHttpUnauthorized(error: unknown): boolean {
  const statusCode =
    typeof (error as { status?: unknown } | null)?.status === "number"
      ? (error as { status: number }).status
      : typeof (error as { statusCode?: unknown } | null)?.statusCode === "number"
        ? (error as { statusCode: number }).statusCode
        : undefined;
  return statusCode === 401;
}

async function loadSignatures(): Promise<void> {
  dbgStep("Lade Signaturen von API...");
  try {
    clearMessage();
    showListSpinner();
    if (!getAccessToken()) {
      recoverSessionAfterAuthLoss("kein Token (abgelaufen oder abgemeldet)", "signatures");
      return;
    }
    const [sigs] = await Promise.all([
      getAllSignatures(),
      loadProfileValues().catch((e) => { dbgWarn(`Profilwerte-Fehler: ${String(e)}`); }),
    ]);
    signatures = sigs;
    dbgOk(`${sigs.length} Signatur(en) geladen`);
    await refreshPlanBadge();
    if (isProSeatBlocked()) {
      renderNoLicenseState();
      return;
    }
    renderSignatureList();
    await updateDefaultSignatureDropdown();
  } catch (error: any) {
    // Status nur als Zahl verwenden (jQuery-jqXHR hat ggf. .status als Funktion)
    const statusCode = typeof error?.status === "number" ? error.status : typeof error?.statusCode === "number" ? error.statusCode : undefined;
    if (statusCode === 401 || isLocalNotSignedInError(error) || !getAccessToken()) {
      recoverSessionAfterAuthLoss(statusCode === 401 ? "401" : "nicht angemeldet", "signatures");
      return;
    }
    const errorDetails = error?.responseText ?? error?.message ?? (typeof error?.status === "number" ? JSON.stringify({ readyState: error?.readyState, status: error?.status, statusText: error?.statusText }) : String(error?.message ?? "Unbekannter Fehler"));
    console.error("Fehler beim Laden der Signaturen:", {
      statusCode,
      readyState: error?.readyState,
      statusText: error?.statusText,
      details: errorDetails,
    });
    const statusStr = statusCode != null ? String(statusCode) : "N/A";
    dbgError(`Signaturen laden fehlgeschlagen – HTTP ${statusStr}: ${String(errorDetails).slice(0, 120)}`);
    showMessage(t("msg.loadError", { status: statusStr, details: errorDetails, apiUrl: API_BASE_URL }));
  }
}

/**
 * Rendert die Liste der Signaturen
 */
function renderSignatureList(): void {
  const container = document.getElementById("signatures-container");
  if (!container) return;

  if (isProSeatBlocked()) {
    renderNoLicenseState();
    return;
  }

  if (signatures.length === 0) {
    container.innerHTML = `<p class="sunnysign-section__title" style="margin-top:0;font-weight:400;font-size:10px;">${t("list.empty")}</p>`;
    return;
  }

  const iconMore = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>';
  const iconPencil = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
  const iconTrash = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>';

  const canEdit = canEditSignatures();

  const menuHtml = (sigId: number) =>
    canEdit
      ? `
        <div class="signature-item-menu-wrap">
          <button type="button" class="signature-item-menu-trigger" data-id="${sigId}" aria-label="${escapeHtml(t("list.menu"))}">${iconMore}</button>
          <div class="signature-item-context-menu" role="menu" aria-hidden="true">
            <button type="button" class="signature-item-menu-edit" data-id="${sigId}" role="menuitem"><span class="signature-item-menu-icon">${iconPencil}</span>${escapeHtml(t("list.edit"))}</button>
            <button type="button" class="signature-item-menu-delete" data-id="${sigId}" role="menuitem"><span class="signature-item-menu-icon">${iconTrash}</span>${escapeHtml(t("list.delete"))}</button>
          </div>
        </div>`
      : "";

  container.innerHTML = signatures
    .map(
      (sig) => `
    <div class="signature-item" data-signature-id="${sig.id}">
      <div class="signature-item-title-row">
        <h3 class="signature-item-title">${escapeHtml(sig.name)}</h3>
        ${menuHtml(sig.id)}
      </div>
      <div class="signature-item-preview">${ensureImageDimensionsForEmail(applyProfileValues(prepareAssetImagesForDisplay(sanitizeSignatureHtml(sig.content))))}</div>
      <div class="signature-item-use-wrap">
        ${
          sig.canUse === false
            ? `<p class="signature-item-not-targeted">${escapeHtml(t("list.notTargeted"))}</p>`
            : `<button type="button" class="sunnysign-btn sunnysign-btn--primary use-button" data-id="${sig.id}">${t("list.use")}</button>`
        }
      </div>
    </div>
  `
    )
    .join("");

  void resolveAssetImagesForDisplay(container);

  const closeOpenMenus = (): void => {
    container.querySelectorAll(".signature-item-context-menu[aria-hidden=false]").forEach((m) => {
      m.setAttribute("aria-hidden", "true");
    });
  };

  if (!(container as HTMLElement & { _menuDelegation?: boolean })._menuDelegation) {
    (container as HTMLElement & { _menuDelegation?: boolean })._menuDelegation = true;
    container.addEventListener("click", (e: Event) => {
      const trigger = (e.target as HTMLElement).closest(".signature-item-menu-trigger");
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        const wrap = (trigger as HTMLElement).closest(".signature-item-menu-wrap");
        const menu = wrap?.querySelector(".signature-item-context-menu") as HTMLElement | null;
        const wasOpen = menu?.getAttribute("aria-hidden") === "false";
        container.querySelectorAll(".signature-item-context-menu[aria-hidden=false]").forEach((m) => {
          m.setAttribute("aria-hidden", "true");
        });
        if (menu && !wasOpen) {
          menu.setAttribute("aria-hidden", "false");
        }
      }
    });
  }

  if (canEdit) {
  container.querySelectorAll(".signature-item-menu-edit").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      (e.currentTarget as HTMLElement).blur();
      closeOpenMenus();
      const id = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
      editSignature(id);
    });
  });

  container.querySelectorAll(".signature-item-menu-delete").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      (e.currentTarget as HTMLElement).blur();
      closeOpenMenus();
      const id = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
      showDeleteConfirmDialog(id);
    });
  });
  }

  container.querySelectorAll(".use-button").forEach((button) => {
    button.addEventListener("click", (e) => {
      const id = parseInt((e.target as HTMLElement).closest("button")?.getAttribute("data-id") || "0");
      useSignature(id);
    });
  });

  if (!(window as any).__sunnysignMenuCloseBound) {
    (window as any).__sunnysignMenuCloseBound = (): void => {
      document.querySelectorAll(".signature-item-context-menu[aria-hidden=false]").forEach((m) => {
        m.setAttribute("aria-hidden", "true");
      });
    };
    document.addEventListener("click", (window as any).__sunnysignMenuCloseBound);
  }
}

/**
 * Zeigt oder versteckt den Consent-Banner im Formular (wenn API 403 wegen fehlender Zustimmung).
 */
function setFormConsentBannerVisible(visible: boolean): void {
  const banner = document.getElementById("form-consent-banner");
  const textEl = document.getElementById("form-consent-banner-text");
  const btnEl = document.getElementById("form-consent-banner-button");
  if (!banner || !textEl || !btnEl) return;
  if (visible) {
    textEl.textContent = t("form.consentBannerText");
    btnEl.textContent = t("form.consentBannerButton");
    btnEl.onclick = () => {
      if (isOutlookWebAppInDesktopSafari()) {
        void startLoginInSameTaskpane({
          forceConsentPromptForSso: true,
          oauthRequireConsent: true,
          oauthPopupCarrier: openOauthBlankPopupForSafariOwa(),
        });
      } else {
        void startLoginInSameTaskpane({
          forceConsentPromptForSso: true,
          oauthRequireConsent: true,
        });
      }
    };
    banner.style.display = "block";
  } else {
    banner.style.display = "none";
  }
}

/**
 * Lädt die Profilwerte neu und aktualisiert den Editor, wenn das Formular sichtbar ist
 * (nach erfolgreicher Zustimmung) – dadurch stimmen auch die Markierungen befüllter Felder.
 */
async function refreshFormProfileFieldsAfterConsent(): Promise<void> {
  const formSection = document.getElementById("signature-form-section");
  if (!formSection || formSection.style.display === "none") return;
  try {
    await loadProfileValues();
    const currentContent = signatureEditorApi?.getContent() ?? "";
    signatureEditorApi = initSignatureEditor("signature-editor-wrap", t, profileFieldOptions(), showMessage, signatureEditorImageOptions());
    signatureEditorApi.setContent(currentContent);
  } catch {
    // Bei Fehler (z. B. Token noch nicht wirksam): Banner bleibt ausgeblendet, Nutzer kann erneut „Zustimmung erteilen“ klicken
  }
}

/** Lädt die Profilwerte für alle Graph-Felder (Vorschau-Ersetzung und Markierung befüllter Felder). */
async function loadProfileValues(): Promise<void> {
  // Office-Werte zuerst, damit sie auch stehen, wenn die API scheitert.
  const officeValues = getOfficeContextProfileValues();
  cachedProfileValues = { ...officeValues };
  const apiValues = await getUserProfileValues(GRAPH_USER_FIELD_IDS);
  // Merge: Office < API (API hat höchste Priorität – Graph-Daten überschreiben Office-Context)
  cachedProfileValues = { ...officeValues, ...apiValues };
}

/**
 * Die Profilfelder für das Editor-Dropdown. Die Liste ist im Add-in hinterlegt; befüllte Felder
 * ergeben sich aus den geladenen Profilwerten.
 */
function profileFieldOptions(): { id: string; label: string; token: string; hasValue?: boolean; value?: string }[] {
  return graphUserFieldOptions().map((field) => {
    const value = cachedProfileValues[field.token]?.trim() ?? "";
    return { ...field, hasValue: value.length > 0, value };
  });
}

/**
 * Stellt für das Formular sicher, dass Profilwerte vorliegen (Markierung befüllter Felder).
 * Fehlt die Zustimmung, erscheint das Banner – das Dropdown funktioniert trotzdem.
 */
async function ensureProfileValuesForForm(): Promise<void> {
  if (Object.keys(cachedProfileValues).length > 0) return;
  try {
    await loadProfileValues();
  } catch (e) {
    if (e instanceof ConsentRequiredError) setFormConsentBannerVisible(true);
  }
}

async function renderSignatureFormCampaignMapping(signatureId: number | null): Promise<void> {
  const wrap = document.getElementById("signature-campaign-mapping-wrap");
  const container = document.getElementById("signature-campaign-mapping");
  if (!wrap || !container) return;

  if (!shouldShowCampaignMappingInForm()) {
    wrap.style.display = "none";
    return;
  }

  wrap.style.display = "";
  try {
    await initSignatureFormCampaignMapping(container, signatureId, t, {
      getSignatureHtml: () =>
        (signatureEditorApi ? signatureEditorApi.getContent() : getSignatureEditorContent()).trim(),
      onValidationChange: () => refreshSignatureFormLengthUi(),
    });
    refreshSignatureFormLengthUi();
  } catch (err) {
    if (isHttpUnauthorized(err) || isLocalNotSignedInError(err) || !getAccessToken()) {
      recoverSessionAfterAuthLoss("Kampagnen-Zuordnung: Session abgelaufen", "signatures");
      return;
    }
    wrap.style.display = "none";
    console.error("Kampagnen-Zuordnung konnte nicht geladen werden:", err);
  }
}

function renderSignatureAudience(signature: Signature | null): void {
  const container = document.getElementById("signature-audience-targeting");
  if (!container) return;
  if (!canEditOrgTemplates()) {
    container.style.display = "none";
    signatureAudiencePicker = null;
    return;
  }
  container.style.display = "";
  signatureAudiencePicker = mountAudiencePicker(
    "signature-audience-targeting",
    t,
    {
      targetAll: signature?.targetAll !== false,
      groups: signature?.targetGroups ?? [],
      isOrgDefaultSignature: signature?.isOrgDefaultSignature === true,
      isOrgReplyForwardDefaultSignature: signature?.isOrgReplyForwardDefaultSignature === true,
    },
    undefined,
    { showOrgDefaults: true }
  );
}

function setSignatureFormLoading(loading: boolean): void {
  const loadingEl = document.getElementById("signature-form-loading");
  const contentEl = document.getElementById("signature-form-content");
  if (loadingEl) loadingEl.style.display = loading ? "flex" : "none";
  if (contentEl) contentEl.style.display = loading ? "none" : "";
}

function setEditorBottomChromeHidden(hidden: boolean): void {
  if (hidden) {
    setBottomChromeVisible(false);
    return;
  }
  applyLicenseGateUi();
}

async function showSignatureForm(): Promise<void> {
  if (!canEditSignatures()) {
    return;
  }
  if (!ensureSessionOrRecover("Neue Signatur: Session abgelaufen", { action: "signature-new" })) {
    return;
  }
  editingSignatureId = null;
  selectedSignature = null;
  document.getElementById("signature-list-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("signature-form-section")!.style.display = "flex";
  setSettingsSectionVisible(false);
  setSignatureFormLoading(true);
  setEditorMainTabsVisible(false);
  setEditorBottomChromeHidden(true);
  setFormConsentBannerVisible(false);
  document.getElementById("form-title")!.textContent = t("form.newTitle");
  const submitBtn = document.getElementById("signature-submit-btn");
  if (submitBtn) submitBtn.textContent = t("form.create");
  (document.getElementById("signature-name") as HTMLInputElement).value = "";
  try {
    await ensureProfileValuesForForm();
    signatureEditorApi = initSignatureEditor("signature-editor-wrap", t, profileFieldOptions(), showMessage, signatureEditorImageOptions());
    signatureEditorApi.setContent("");
    renderSignatureAudience(null);
    await renderSignatureFormCampaignMapping(null);
    hideMessage();
    applyPremiumTeaserVisibility();
    updateHeaderAuthUi();
  } catch (err) {
    if (isHttpUnauthorized(err) || isLocalNotSignedInError(err) || !getAccessToken()) {
      recoverSessionAfterAuthLoss("Neue Signatur: Session abgelaufen", { action: "signature-new" });
      return;
    }
    throw err;
  } finally {
    setSignatureFormLoading(false);
  }
}

/**
 * Zeigt die Signatur-Liste an
 */
function showSignatureList(): void {
  if (isFreCarouselOpen()) return;
  document.getElementById("signature-list-section")!.style.display = "block";
  document.getElementById("signature-form-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("campaigns-list-section")!.style.display = "none";
  document.getElementById("campaign-form-section")!.style.display = "none";
  setSettingsSectionVisible(false);
  setActiveMainTab("signatures");
  setEditorMainTabsVisible(true);
  setEditorBottomChromeHidden(false);
  loadSignatures();
  applyPremiumTeaserVisibility();
  updateHeaderAuthUi();
}

async function showSettingsSection(): Promise<void> {
  if (isFreCarouselOpen()) return;
  if (!ensureSessionOrRecover("Einstellungen: Session abgelaufen", "settings")) {
    return;
  }
  document.getElementById("consent-section")!.style.display = "none";
  document.getElementById("signature-list-section")!.style.display = "none";
  document.getElementById("signature-form-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("campaigns-list-section")!.style.display = "none";
  document.getElementById("campaign-form-section")!.style.display = "none";
  setEditorMainTabsVisible(false);
  setBottomChromeVisible(false);
  setSettingsSectionVisible(true);
  const blocked = isProSeatBlocked();
  const defaultControls = document.getElementById("default-signature-controls");
  if (defaultControls) defaultControls.style.display = blocked ? "none" : "flex";
  updateHeaderAuthUi();
  updateConsentTutorialButtonUi();
  if (!blocked) {
    try {
      await updateDefaultSignatureDropdown();
    } catch (err) {
      if (isHttpUnauthorized(err) || isLocalNotSignedInError(err) || !getAccessToken()) {
        recoverSessionAfterAuthLoss("Einstellungen laden: Session abgelaufen", "settings");
      }
    }
  }
  applyPremiumTeaserVisibility();
}

/**
 * Bearbeitet eine bestehende Signatur.
 * Lädt Profilfeld-Optionen von der API (AAD/Graph) für das Dropdown.
 * Bei 403 (Consent erforderlich): Banner „Zustimmung erteilen“ anzeigen.
 */
async function editSignature(id: number): Promise<void> {
  if (!canEditSignatures()) return;
  if (!ensureSessionOrRecover("Signatur bearbeiten: Session abgelaufen", { action: "signature-edit", id })) {
    return;
  }
  const signature = signatures.find((sig) => sig.id === id);
  if (!signature) return;

  editingSignatureId = id;
  selectedSignature = signature;
  document.getElementById("signature-list-section")!.style.display = "none";
  document.getElementById("signature-preview-section")!.style.display = "none";
  document.getElementById("signature-form-section")!.style.display = "flex";
  setSettingsSectionVisible(false);
  setSignatureFormLoading(true);
  setEditorMainTabsVisible(false);
  setEditorBottomChromeHidden(true);
  setFormConsentBannerVisible(false);
  document.getElementById("form-title")!.textContent = t("form.editTitle");
  const submitBtn = document.getElementById("signature-submit-btn");
  if (submitBtn) submitBtn.textContent = t("form.save");
  (document.getElementById("signature-name") as HTMLInputElement).value = signature.name;
  try {
    await ensureProfileValuesForForm();
    signatureEditorApi = initSignatureEditor("signature-editor-wrap", t, profileFieldOptions(), showMessage, signatureEditorImageOptions());
    signatureEditorApi.setContent(signature.content);
    renderSignatureAudience(signature);
    await renderSignatureFormCampaignMapping(id);
    hideMessage();
    applyPremiumTeaserVisibility();
    updateHeaderAuthUi();
  } catch (err) {
    if (isHttpUnauthorized(err) || isLocalNotSignedInError(err) || !getAccessToken()) {
      recoverSessionAfterAuthLoss("Signatur bearbeiten: Session abgelaufen", { action: "signature-edit", id });
      return;
    }
    throw err;
  } finally {
    setSignatureFormLoading(false);
  }
}

let _deleteDialogOpener: HTMLElement | null = null;

/** Öffnet den Löschen-Bestätigungsdialog (window.confirm ist in Office Add-ins nicht unterstützt). */
function showDeleteConfirmDialog(id: number): void {
  if (!canEditSignatures()) return;
  if (!ensureSessionOrRecover("Signatur löschen: Session abgelaufen", "signatures")) {
    return;
  }
  pendingDeleteId = id;
  _deleteDialogOpener = document.activeElement as HTMLElement | null;
  const dialog = document.getElementById("delete-confirm-dialog");
  if (!dialog) return;
  dialog.style.display = "flex";
  // Fokus auf ersten Button setzen
  const firstBtn = dialog.querySelector<HTMLButtonElement>("button");
  firstBtn?.focus();
  // Focus Trap: Tab/Shift+Tab innerhalb des Dialogs halten
  dialog.addEventListener("keydown", _trapDeleteDialogFocus);
}

function closeDeleteConfirmDialog(): void {
  pendingDeleteId = null;
  const dialog = document.getElementById("delete-confirm-dialog");
  if (!dialog) return;
  dialog.removeEventListener("keydown", _trapDeleteDialogFocus);
  dialog.style.display = "none";
  // Fokus zurück zum auslösenden Element
  _deleteDialogOpener?.focus();
  _deleteDialogOpener = null;
}

function _trapDeleteDialogFocus(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    if (signatureDeleteLock.inFlight) {
      e.preventDefault();
      return;
    }
    closeDeleteConfirmDialog();
    return;
  }
  if (e.key !== "Tab") return;
  const dialog = document.getElementById("delete-confirm-dialog");
  if (!dialog) return;
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>("button, [href], input, [tabindex]:not([tabindex='-1'])")).filter(el => !el.hasAttribute("disabled"));
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey) {
    if (document.activeElement === first) { last.focus(); e.preventDefault(); }
  } else {
    if (document.activeElement === last) { first.focus(); e.preventDefault(); }
  }
}

async function onDeleteConfirmYes(): Promise<void> {
  if (pendingDeleteId === null || signatureDeleteLock.inFlight) return;
  const id = pendingDeleteId;
  const yesBtn = document.getElementById("delete-confirm-yes") as HTMLButtonElement | null;
  const noBtn = document.getElementById("delete-confirm-no") as HTMLButtonElement | null;

  await runWithApiBusy({ buttons: [yesBtn, noBtn], lock: signatureDeleteLock }, async () => {
    try {
      clearMessage();
      await deleteSignature(id);
      closeDeleteConfirmDialog();
      showMessage(t("msg.deleteSuccess"));
      loadSignatures();
    } catch (error) {
      showMessage(t("msg.deleteError"));
      console.error("Fehler beim Löschen:", error);
    }
  });
}

/**
 * Zeigt den Löschen-Dialog (Löschen-Klick)
 */
function deleteSignatureById(id: number): void {
  showDeleteConfirmDialog(id);
}

/**
 * Verwendet eine Signatur: fügt sie direkt in die E-Mail ein (ohne Vorschau).
 */
function useSignature(id: number): void {
  if (isProSeatBlocked()) return;
  if (!ensureSessionOrRecover("Signatur einfügen: Session abgelaufen", "signatures")) {
    return;
  }
  const signature = signatures.find((sig) => sig.id === id);
  if (!signature) return;

  selectedSignature = signature;
  insertSignatureIntoEmail();
}

/**
 * Behandelt das Absenden des Formulars
 */
const signatureSaveLock: ApiBusyLock = { inFlight: false };
const signatureDeleteLock: ApiBusyLock = { inFlight: false };
const defaultSignatureSaveLock: ApiBusyLock = { inFlight: false };

async function handleFormSubmit(e: Event): Promise<void> {
  e.preventDefault();
  if (signatureSaveLock.inFlight) return;
  if (!ensureSessionOrRecover(
    "Signatur speichern: Session abgelaufen",
    editingSignatureId != null
      ? { action: "signature-edit", id: editingSignatureId }
      : { action: "signature-new" }
  )) {
    return;
  }

  const name = (document.getElementById("signature-name") as HTMLInputElement).value.trim();
  const content = (signatureEditorApi ? signatureEditorApi.getContent() : getSignatureEditorContent()).trim();
  console.log("[Add-in] Signatur gespeichert (nach Sanitize):", content);
  const sanitizeBlocked = signatureEditorApi?.didBlockDisallowedContent?.() === true;
  const audience = signatureAudiencePicker?.getState() ?? {
    targetAll: true,
    groups: [],
    isOrgDefaultSignature: false,
    isOrgReplyForwardDefaultSignature: false,
  };

  if (!name || !content) {
    showMessage(t("msg.fillAll"));
    return;
  }

  if (content.length > SIGNATURE_SAVE_MAX_CHARS) {
    showMessage(
      t("msg.tooLong", {
        len: String(content.length),
        saveMax: String(SIGNATURE_SAVE_MAX_CHARS),
        max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
      })
    );
    refreshSignatureFormLengthUi();
    return;
  }

  if (shouldShowCampaignMappingInForm()) {
    const mappingStatus = getSignatureFormMappingLengthStatus(content);
    if (mappingStatus.overSlot) {
      showMessage(
        t("msg.mappingBlocked", {
          len: String(mappingStatus.estimated),
          max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
        })
      );
      refreshSignatureFormLengthUi();
      return;
    }
  }

  const submitBtn = document.getElementById("signature-submit-btn") as HTMLButtonElement | null;
  await runWithApiBusy(
    {
      buttons: [submitBtn],
      lock: signatureSaveLock,
      afterUnlock: () => refreshSignatureFormLengthUi(),
    },
    async () => {
      try {
        clearMessage();

        let savedSignatureId = editingSignatureId;

        if (editingSignatureId !== null) {
          const signatureToUpdate: Signature = {
            id: editingSignatureId,
            name,
            content,
            createdAt: selectedSignature!.createdAt,
            updatedAt: new Date().toISOString(),
            targetAll: audience.targetAll,
            targetGroupIds: audience.groups.map((group) => group.id),
            targetGroups: audience.groups,
            isOrgDefaultSignature: audience.isOrgDefaultSignature === true,
            isOrgReplyForwardDefaultSignature: audience.isOrgReplyForwardDefaultSignature === true,
          };
          await updateSignature(signatureToUpdate);
          showMessage(
            sanitizeBlocked
              ? `${t("msg.updateSuccess")} ${t("editor.contentSanitized")}`
              : t("msg.updateSuccess")
          );
        } else {
          const created = await createSignature({
            name,
            content,
            targetAll: audience.targetAll,
            targetGroupIds: audience.groups.map((group) => group.id),
            isOrgDefaultSignature: audience.isOrgDefaultSignature === true,
            isOrgReplyForwardDefaultSignature: audience.isOrgReplyForwardDefaultSignature === true,
          });
          savedSignatureId = created.id;
          showMessage(
            sanitizeBlocked
              ? `${t("msg.createSuccess")} ${t("editor.contentSanitized")}`
              : t("msg.createSuccess")
          );
        }

        if (shouldShowCampaignMappingInForm() && savedSignatureId !== null) {
          try {
            await updateSignatureCampaignMappings(savedSignatureId, getSignatureFormSelectedCampaignIds());
          } catch (mappingErr) {
            console.error("Kampagnen-Zuordnung konnte nicht gespeichert werden:", mappingErr);
            showMessage(`${t("campaigns.mappingSaveFailed")}: ${String(mappingErr)}`);
          }
        }

        showSignatureList();
      } catch (error) {
        if (isHttpUnauthorized(error) || isLocalNotSignedInError(error) || !getAccessToken()) {
          recoverSessionAfterAuthLoss(
            "Signatur speichern: Session abgelaufen",
            editingSignatureId != null
              ? { action: "signature-edit", id: editingSignatureId }
              : { action: "signature-new" }
          );
          return;
        }
        showMessage(t("msg.saveError"));
        console.error("Fehler beim Speichern:", error);
      }
    }
  );
}

/**
 * Holt den E-Mail-Body per getAsync (optional mit bodyMode für nur aktuellen Antworttext).
 * In Classic Outlook/Mac darf kein options-Objekt übergeben werden (führt zu Sys.ArgumentException).
 * Bei Bedarf nur 2 Argumente: getAsync(coercionType, callback).
 */
/** Klassisches Outlook Win32-Compose (nicht OWA/Neu/Mobil) — gleiche Heuristik wie Autorun compose-runtime. */
function isOutlookClassicWindowsCompose(): boolean {
  if (isMobileOutlookHost()) return false;
  if (isWebOrNewOutlookHost()) return false;
  try {
    const hn = String(Office.context?.mailbox?.diagnostics?.hostName ?? "").trim().toLowerCase();
    if (!(hn === "outlook" || hn === "")) return false;
    const plat = Office.context?.diagnostics?.platform;
    if (plat === undefined || plat === null) return true;
    return plat === Office.PlatformType.PC;
  } catch {
    return false;
  }
}

/** OWA kann `{ composeType, coercionType }` liefern statt flachem Enum-Wert. */
function normalizeGetComposeTypeApiValue(
  value: unknown
): Office.MailboxEnums.ComposeType | string | null {
  if (value == null) return null;
  if (typeof value === "object" && value !== null && "composeType" in value) {
    const inner = (value as { composeType?: unknown }).composeType;
    if (inner == null) return null;
    return inner as Office.MailboxEnums.ComposeType | string;
  }
  return value as Office.MailboxEnums.ComposeType;
}

/** Compose-Typ (Antwort/Neu/…); fehlt z. B. auf älteren Builds → null. */
function getComposeTypeAsyncSafe(): Promise<Office.MailboxEnums.ComposeType | null> {
  const mailItem = Office.context?.mailbox?.item as
    | (Office.MessageCompose & {
        getComposeTypeAsync?: (
          options: Office.AsyncContextOptions,
          callback: (asyncResult: Office.AsyncResult<Office.MailboxEnums.ComposeType>) => void
        ) => void;
      })
    | undefined;
  if (!mailItem?.getComposeTypeAsync) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    try {
      mailItem.getComposeTypeAsync({}, (r) => {
        if (r.status !== Office.AsyncResultStatus.Succeeded || r.value == null) {
          resolve(null);
          return;
        }
        resolve(normalizeGetComposeTypeApiValue(r.value) as Office.MailboxEnums.ComposeType | null);
      });
    } catch {
      resolve(null);
    }
  });
}

function getBodyAsync(options?: { bodyMode?: number }): Promise<string> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox?.item;
    if (!item?.body?.getAsync) {
      reject(new Error("Body API nicht verfügbar"));
      return;
    }
    const callback = (result: Office.AsyncResult<string>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value ?? "");
      } else {
        reject(result.error ?? new Error("getAsync fehlgeschlagen"));
      }
    };
    const useBodyMode = options?.bodyMode != null;
    if (useBodyMode) {
      try {
        item.body.getAsync(
          Office.CoercionType.Html,
          { bodyMode: options!.bodyMode! },
          callback
        );
      } catch (e) {
        console.warn("[Signatur] getAsync mit bodyMode fehlgeschlagen (z. B. Classic Outlook):", e);
        reject(e);
      }
    } else {
      item.body.getAsync(Office.CoercionType.Html, callback);
    }
  });
}

/** BodyMode.HostConfig: nur aktueller Antworttext (nur Web/Neues Outlook/Mobile, in Classic/Mac ignoriert). */
const BODY_MODE_HOST_CONFIG = (Office as any).MailboxEnums?.BodyMode?.HostConfig ?? 1;

/**
 * Sucht im E-Mail-Body nach dem Beginn des zitierten E-Mail-Verlaufs (Antworten/Weiterleitungen).
 * Gibt den Index zurück, an dem der Verlauf beginnt, oder null wenn kein Verlauf erkannt wird.
 * Erweiterte Marker für klassisches und neues Outlook (inkl. border-left, WordSection, etc.).
 */
function findQuotedContentStart(body: string): number | null {
  if (!body || typeof body !== "string") return null;
  // Nach Doppelpunkt \s* (nicht \s), damit "Von:</b>" oder "Gesendet:</strong>" erkannt werden.
  // Zusätzlich: Label und Doppelpunkt können durch Tags getrennt sein (z. B. <b>Von</b>:).
  const patterns: RegExp[] = [
    /<div[^>]*\bid\s*=\s*["']appendonsend["'][^>]*>/i,
    /<div[^>]*style\s*=\s*["'][^"']*border-left\s*:/i,
    /<div[^>]*\bclass\s*=\s*["'][^"']*quote/i,
    /-{3,}\s*Original\s+Message\s*-{3,}/i,
    /-{3,}\s*Ursprüngliche\s+Nachricht\s*-{3,}/i,
    /Von\s*:\s*/i,
    /Von(?:<\/[^>]+>)*\s*:\s*/i,
    /From\s*:\s*/i,
    /From(?:<\/[^>]+>)*\s*:\s*/i,
    /Gesendet\s*:\s*/i,
    /Gesendet(?:<\/[^>]+>)*\s*:\s*/i,
    /Sent\s*:\s*/i,
    /Sent(?:<\/[^>]+>)*\s*:\s*/i,
    /An\s*:\s*/i,
    /An(?:<\/[^>]+>)*\s*:\s*/i,
    /To\s*:\s*/i,
    /To(?:<\/[^>]+>)*\s*:\s*/i,
    /Betreff\s*:\s*/i,
    /Betreff(?:<\/[^>]+>)*\s*:\s*/i,
    /Subject\s*:\s*/i,
    /Subject(?:<\/[^>]+>)*\s*:\s*/i,
    /Am\s+[\s\S]{1,120}?schrieb\s/i,
    /On\s+[\s\S]{1,120}?wrote\s*:\s*/i,
    /<blockquote[\s>]/i,
    /<hr\b[^>]*>/i,
    /<div[^>]*\bclass\s*=\s*["']WordSection1["'][^>]*>/i,
  ];
  let minIndex: number | null = null;
  let matchedPattern: string | null = null;
  for (const re of patterns) {
    const m = body.match(re);
    if (m && m.index !== undefined) {
      if (minIndex === null || m.index < minIndex) {
        minIndex = m.index;
        matchedPattern = re.source;
      }
    }
  }
  if (minIndex === null && body.length > 0) {
    const snippet = body.slice(0, 800).replace(/\s+/g, " ");
    console.warn("[Signatur] Kein Verlauf-Marker gefunden. Body-Anfang (800 Zeichen):", snippet);
  } else if (matchedPattern) {
    console.log("[Signatur] Verlauf-Marker gefunden bei Index", minIndex, "Pattern:", matchedPattern.slice(0, 50));
  }
  return minIndex;
}

/**
 * Ermittelt die Einfügeposition der Signatur: vor dem Verlauf bei Antworten, sonst am Ende.
 * Nutzt bodyMode API wo unterstützt (Web/Neues Outlook), sonst Mustererkennung (Classic/Mac).
 * Hinweis: Klassisches Outlook bei Antwort/Weiterleitung setzt insertSignatureIntoEmail die Position
 * separat auf 0 (ganz oben), weil die Mustererkennung dort oft versagt und sonst ans Ende fällt.
 */
function getSignatureInsertPosition(fullBody: string, replyOnly: string | null): number {
  if (replyOnly != null && replyOnly.length > 0 && replyOnly.length < fullBody.length) {
    const trimmedReply = replyOnly.trim();
    const trimmedFull = fullBody.trim();
    if (trimmedFull.startsWith(trimmedReply)) {
      const pos = fullBody.indexOf(trimmedReply);
      return pos >= 0 ? pos + trimmedReply.length : fullBody.length;
    }
    const idx = fullBody.indexOf(replyOnly);
    if (idx >= 0) return idx + replyOnly.length;
  }
  const quotedStart = findQuotedContentStart(fullBody);
  return quotedStart !== null ? quotedStart : fullBody.length;
}

/**
 * Fügt die ausgewählte Signatur ein.
 * - Bei Antworten: vor dem E-Mail-Verlauf (Web/Neu/Mobile) bzw. Mustererkennung (Mac).
 * - Klassisches Outlook (Win32) bei Antwort/Weiterleitung/Antwort an alle: Signatur ganz oben im Body.
 * - Bei neuer E-Mail: ganz am Ende des Body (außer leerer Body).
 * Lädt die gerenderte Signatur von der API (Platzhalter → User-Daten aus Graph), bei Fehler Fallback auf Roh-Content.
 */
async function insertSignatureIntoEmail(): Promise<void> {
  if (isProSeatBlocked()) return;
  if (!ensureSessionOrRecover("Signatur einfügen: Session abgelaufen", "signatures")) {
    return;
  }
  if (!selectedSignature) {
    showMessage(t("msg.selectFirst"));
    return;
  }

  try {
    clearMessage();

    const item = Office.context.mailbox.item;
    if (!item) {
      showMessage(t("msg.openEmail"));
      return;
    }

    let content: string;
    let fromApi = false;
    try {
      content = await getRenderedSignature(selectedSignature.id);
      fromApi = !!content;
      if (!content) content = selectedSignature.content;
    } catch (err) {
      // Zielgruppen-Verbot: kein Fallback auf den gecachten Inhalt, sonst wäre die Sperre umgehbar.
      if (err instanceof SignatureNotTargetedError) {
        showMessage(t("msg.notTargeted"));
        return;
      }
      if (isHttpUnauthorized(err) || isLocalNotSignedInError(err) || !getAccessToken()) {
        recoverSessionAfterAuthLoss("Signatur einfügen: Session abgelaufen", "signatures");
        return;
      }
      content = selectedSignature.content;
    }
    // Client-seitiger Fallback: verbleibende {{Platzhalter}} mit gecachten Profilwerten füllen
    if (content && content.includes("{{")) {
      content = applyProfileValues(content);
    }
    const snippet = (s: string) => (s.length > 500 ? s.slice(0, 500) + "…" : s);
    console.log("[Use] Vor Verarbeitung (fromApi:", fromApi, ", Länge:", content?.length, "):", snippet(content ?? ""));
    let contentToInsert = fromApi ? content : sanitizeSignatureHtml(content);
    if (isOutlookClassicWindowsCompose()) {
      contentToInsert = prepareClassicOutlookSignatureInsert(contentToInsert, (h) =>
        appendWatermarkIfNeeded(h, cachedCapabilities, cachedUserPlan)
      );
    } else {
      contentToInsert = prepareSignatureHtmlForOutlookCompose(contentToInsert);
      contentToInsert = appendWatermarkIfNeeded(contentToInsert, cachedCapabilities, cachedUserPlan);
    }
    console.log("[Use] Vor setAsync (Länge):", contentToInsert?.length, snippet(contentToInsert ?? ""));

    contentToInsert = await resolveInlineAttachmentsForCompose(contentToInsert, item as Office.MessageCompose);

    // Product-fixed: always prefer Signatur-Slot (setSignatureAsync).
    // Body-Einfügen (manualMerge) only as fallback when over slot limit or Office.js fails
    // (also covers mobile when setSignatureAsync is unavailable/fails).
    const preferOfficeJsSlot = contentToInsert.length <= SET_SIGNATURE_ASYNC_MAX_CHARS;
    if (!preferOfficeJsSlot) {
      console.log(
        "[Use] HTML über Slot-Limit (" +
          contentToInsert.length +
          ") → Fallback Body-Einfügen (manualMerge)"
      );
    }
    if (preferOfficeJsSlot) {
      const tryOfficeJsInsert = (): Promise<boolean> =>
        new Promise((resolve) => {
          const body = item.body as Office.Body & {
            setSignatureAsync?: (
              data: string,
              options: Office.AsyncContextOptions & { coercionType: Office.CoercionType },
              callback: (r: Office.AsyncResult<void>) => void
            ) => void;
            setSelectedDataAsync?: (
              data: string,
              options: Office.AsyncContextOptions & { coercionType: Office.CoercionType },
              callback: (r: Office.AsyncResult<void>) => void
            ) => void;
          };
          const finishOk = (): void => {
            showMessage(t("msg.insertSuccess"));
            resolve(true);
          };
          const trySelected = (): void => {
            if (typeof body.setSelectedDataAsync !== "function") {
              resolve(false);
              return;
            }
            body.setSelectedDataAsync(contentToInsert, { coercionType: Office.CoercionType.Html }, (r) => {
              if (r.status === Office.AsyncResultStatus.Succeeded) {
                console.log("[Use] officeJs setSelectedDataAsync OK");
                finishOk();
              } else {
                console.warn("[Use] officeJs setSelectedDataAsync failed:", r.error);
                resolve(false);
              }
            });
          };
          if (typeof body.setSignatureAsync === "function") {
            body.setSignatureAsync(contentToInsert, { coercionType: Office.CoercionType.Html }, (r) => {
              if (r.status === Office.AsyncResultStatus.Succeeded) {
                console.log("[Use] officeJs setSignatureAsync OK");
                finishOk();
              } else {
                console.warn("[Use] officeJs setSignatureAsync failed → setSelectedDataAsync:", r.error);
                trySelected();
              }
            });
            return;
          }
          trySelected();
        });

      if (await tryOfficeJsInsert()) return;
      console.log("[Use] officeJs fehlgeschlagen → Fallback Body-Einfügen (manualMerge)");
    }

    console.log("[Signatur] Lade Body (voll)...");
    const fullBody = await getBodyAsync();
    console.log("[Signatur] Body geladen, Länge:", fullBody.length);

    let replyOnly: string | null = null;
    try {
      console.log("[Signatur] Versuche bodyMode (nur aktueller Antworttext)...");
      const withMode = await getBodyAsync({ bodyMode: BODY_MODE_HOST_CONFIG });
      if (withMode.length < fullBody.length) {
        replyOnly = withMode;
        console.log("[Signatur] bodyMode OK, replyOnly Länge:", replyOnly.length);
      } else {
        console.log("[Signatur] bodyMode lieferte gleiche/vollständige Länge, nutze Mustererkennung");
      }
    } catch (e) {
      console.warn("[Signatur] bodyMode nicht verfügbar (z. B. Classic Outlook), nutze Mustererkennung:", e);
    }

    let insertAt = getSignatureInsertPosition(fullBody, replyOnly);
    const composeType = await getComposeTypeAsyncSafe();
    const composeTypeNewMail = Office.MailboxEnums.ComposeType.NewMail;
    const quotedStartIdx = findQuotedContentStart(fullBody);
    const isClassicWin = isOutlookClassicWindowsCompose();
    /*
     * Classic: getComposeTypeAsync fehlt oft oder liefert "newMail" trotz Antwort.
     * Zitat-Header (Von:/From:/…) = sicherer Hinweis auf Antwort → Signatur immer Zeile 1.
     */
    const typedNonNewMail =
      composeType != null && composeType !== composeTypeNewMail;
    const classicReplyTop =
      isClassicWin && (quotedStartIdx !== null || typedNonNewMail);
    if (classicReplyTop) {
      insertAt = 0;
      console.log(
        "[Signatur] Classic: Einfügen ganz oben (insertAt=0). ComposeType=",
        composeType,
        "quotedIdx=",
        quotedStartIdx
      );
    }
    const quotedStart = quotedStartIdx;
    console.log("[Signatur] Einfügeposition:", insertAt, "von", fullBody.length, "| Verlauf-Marker gefunden:", quotedStart !== null);

    const trimmedBody = fullBody.trim().length > 0;
    const separator = trimmedBody ? "<br><br>" : "";
    let newBody: string;
    if (insertAt === 0 && trimmedBody) {
      newBody = contentToInsert + separator + fullBody;
    } else {
      const signatureBlock = separator + contentToInsert;
      newBody = fullBody.slice(0, insertAt) + signatureBlock + fullBody.slice(insertAt);
    }

    await new Promise<void>((resolve, reject) => {
      item.body.setAsync(newBody, { coercionType: Office.CoercionType.Html }, (setResult: Office.AsyncResult<void>) => {
        if (setResult.status === Office.AsyncResultStatus.Succeeded) {
          showMessage(t("msg.insertSuccess"));
          resolve();
        } else {
          showMessage(t("msg.insertError", { error: setResult.error?.message || "" }));
          reject(setResult.error);
        }
      });
    });
  } catch (error) {
    if (isHttpUnauthorized(error) || isLocalNotSignedInError(error) || !getAccessToken()) {
      recoverSessionAfterAuthLoss("Signatur einfügen: Session abgelaufen", "signatures");
      return;
    }
    showMessage(t("msg.insertGenericError"));
    console.error("Fehler:", error);
  }
}

/**
 * Escaped HTML-Zeichen für sichere Anzeige
 */
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
